/**
 * English is the source of truth: every other dictionary is typed against it,
 * so adding a key here and forgetting to translate it fails the build rather
 * than falling back silently at runtime.
 *
 * Scheduler (`s2.*`) keys live with the rest of the product copy.
 */
export const EN = {
  // Shell
  'nav.dashboard': 'Dashboard',
  'nav.schedule': 'Scheduler',
  'nav.users': 'Users',
  'nav.projects': 'Projects',
  'nav.profile': 'My Account',
  'nav.settings': 'Settings',
  'nav.myApps': 'My Apps',
  'nav.messageTemplates': 'Messages',
  'nav.account': 'Account',
  'nav.primary': 'Primary',
  'nav.support': 'Support',
  'nav.ai': 'Ask Celeri AI',
  'nav.askAi': 'Ask AI',
  'ai.newChat': 'New chat',
  'ai.expand': 'Full screen',
  'ai.collapse': 'Exit full screen',
  'ai.intro.title': 'What can I do for you?',
  'ai.intro.text':
    'I can fill multi-shifts with people by position, report late arrivals and missed check-outs, create projects with multi-shifts, and create tasks. Changes wait for your confirmation.',
  'ai.suggestion.fill': 'Fill the Bartender slots on Friday',
  'ai.suggestion.late': 'Who was late last week?',
  'ai.suggestion.checkout': 'Who forgot to check out this month?',
  'ai.suggestion.project':
    'Create project Summer Gala on 14.11 at Main hall and Terrace with Bartender x3 18:00-02:00; Waiter x2 18-23 @ Terrace',
  'ai.suggestion.task': 'Create a task to order ice by Friday, high priority',
  'ai.placeholder': 'Ask Celeri AI…',
  'ai.send': 'Send',
  'ai.thinking': 'Thinking… this can take a little while',
  'ai.report.download': 'Download CSV',
  'ai.query.show': 'Show the query',
  'ai.proposal.confirming': 'Working on it…',
  'ai.proposal.done': 'Done',
  'ai.proposal.cancelled': 'Cancelled',
  'ai.proposal.expired': 'Expired: ask again',
  'ai.proposal.failed': 'Not completed',
  'ai.done': 'Done.',
  'ai.error.unavailable': 'Celeri AI is not available right now. Try again in a moment.',
  'ai.error.noWorkspace': 'Select a workspace first.',
  'ai.error.spent': 'That plan was already handled or has expired. Ask again to get a fresh one.',
  'ai.error.forbidden': "You don't have access to Celeri AI.",
  'ai.error.generic': 'Something went wrong. Try again.',
  'ai.error.empty': 'I have no answer to that.',
  'ai.voice.start': 'Speak your request',
  'ai.voice.stop': 'Stop and send',
  'ai.voice.cancel': 'Cancel recording',
  'ai.voice.listening': 'Listening…',
  'ai.voice.transcribing': 'Turning your words into text…',
  'ai.voice.spoken': 'Spoken',
  'ai.voice.empty': "I didn't catch any words. Try again a little closer to the microphone.",
  'ai.voice.unavailable': 'Voice requests are not available right now. Type your request instead.',
  'ai.voice.tooLong': 'That recording is too long. Keep it under two minutes.',
  'ai.voice.unsupported': "This browser can't record audio. Type your request instead.",
  'ai.voice.denied':
    'Microphone access is blocked. Allow it in the browser’s site settings and try again.',
  'ai.voice.noMicrophone': 'No microphone was found.',
  'ai.voice.failed': "The recording didn't work. Try again.",
  'nav.notifications': 'Notifications',
  'nav.expandSidebar': 'Expand sidebar',
  'nav.collapseSidebar': 'Collapse sidebar',
  'nav.openNavigation': 'Open navigation',
  'nav.closeNavigation': 'Close navigation',
  'nav.menu': 'Menu',
  'nav.skipToContent': 'Skip to main content',

  // Account menu
  'account.open': 'Open account menu',
  'account.myAccount': 'My Account',
  'account.myApps': 'My Apps',
  'account.companySettings': 'Company Settings',
  'account.language': 'Language',
  'account.theme': 'Theme',
  'account.logout': 'Log out',
  'account.loggingOut': 'Logging out…',
  'account.addCompany': 'Add new company',

  // Workplace
  'workplace.change': 'Change workplace',
  'workplace.switched': 'Switched to {name}.',
  'workplace.noBranch': '{name} has no branch to switch into.',

  // Projects
  'projects.change': 'Change project',
  'projects.select': 'Select project',
  'projects.empty': 'No projects yet',
  'projects.new': 'New project',
  'projects.edit': 'Edit project',
  'projects.delete': 'Delete project',
  'projects.deleteConfirm': 'Delete {name}?',
  'projects.deleteMessage':
    'All data related to this project will be lost. Project history is kept for audit.',
  'projects.created': 'Created {name}.',
  'projects.updated': 'Updated {name}.',
  'projects.deleted': 'Deleted {name}.',
  'projects.fieldColor': 'Project color',
  'projects.palette.cobalt': 'Cobalt (default)',
  'projects.palette.amethyst': 'Amethyst',
  'projects.palette.lagoon': 'Lagoon',
  'projects.palette.emerald': 'Emerald',
  'projects.palette.lime': 'Lime',
  'projects.palette.sunflower': 'Sunflower',
  'projects.palette.tangerine': 'Tangerine',
  'projects.palette.scarlet': 'Scarlet',
  'projects.palette.slate': 'Slate',
  'projects.palette.custom': 'Saved color',
  'projects.fieldName': 'Name',
  'projects.fieldStart': 'Start date',
  'projects.fieldEnd': 'End date',
  'projects.validation.required': 'This field is required.',
  'projects.validation.tooLong': 'Name must be at most 200 characters.',
  'projects.validation.invalidDate': 'Use a valid date.',
  'projects.validation.beforeStart': 'End date cannot be before the start date.',
  'projects.emptyHint': 'Create a project to plan its tasks and shifts on their own.',
  'projects.loading': 'Loading projects…',
  'projects.loadError': 'Could not load the projects',
  'projects.noApps': 'No app that works with projects is switched on for this workplace.',
  'projects.notFound.title': 'Project not found',
  'projects.notFound.text':
    'This project does not exist in this workplace, or it has been deleted.',
  'projects.back': 'Back to projects',

  // Theme
  'theme.label': 'Theme: {mode}',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.system': 'System',

  // Auth — shared
  'auth.email': 'Email',
  'auth.emailPlaceholder': 'email@example.com',
  'auth.password': 'Password',
  'auth.passwordPlaceholder': 'Enter password',
  'auth.passwordRepeat': 'Repeat password',
  'auth.showPassword': 'Show password',
  'auth.hidePassword': 'Hide password',
  'auth.aboutCeleri': 'About Celeri',
  'auth.help': 'Help',
  'auth.support': 'Support',
  'auth.backToLogin': 'Back to sign in',

  // Auth — login help (guest support drawer)
  'support.loginHelp.title': 'Need help with login or registration?',
  'support.loginHelp.email': 'Email',
  'support.loginHelp.emailPlaceholder': 'Enter your email address',
  'support.loginHelp.subject': 'Subject',
  'support.loginHelp.subjectPlaceholder': 'Brief summary of the issue',
  'support.loginHelp.message': 'Message',
  'support.loginHelp.messagePlaceholder': 'Describe your issue with login or registration',
  'support.loginHelp.captcha': 'Solve: {question} = ?',
  'support.loginHelp.send': 'Send',
  'support.loginHelp.toast.captchaFailed': 'Incorrect answer. Please try again.',
  'support.loginHelp.toast.sent': "Message sent! We'll contact you soon.",

  // In-app support (signed-in drawer)
  'support.home.title': 'How can we help you?',
  'support.home.suggested': 'Suggested for this page',
  'support.home.bug.label': 'Report a Bug',
  'support.home.bug.description': 'Something is not working as expected',
  'support.home.faq.label': 'Ask a Question',
  'support.home.faq.description': 'Browse our FAQ and find answers',
  'support.home.contact.label': 'Feedback',
  'support.home.contact.description': 'Leave a message or suggest an improvement',
  'support.tip.hourlyPayments':
    'Bulk-import employees from Tripletex, Systima, or CSV to process payments faster.',
  'support.tip.fixedSalary':
    'Fixed salary cycles can run daily, weekly, or monthly — set it once and it runs automatically.',
  'support.tip.timeTracking':
    'Employees can also submit time entries directly from the Celeri mobile app.',
  'support.tip.users':
    'Import your entire team from your accounting system instead of adding them one by one.',
  'support.tip.integrations':
    'Connecting ZTL enables real-time bank transfers directly from Celeri — no manual steps.',
  'support.tip.profile':
    'Complete your national ID and bank account details to become "Ready for Payroll".',
  'support.tip.history':
    'You can export your full payment history as a report from the History page.',
  'support.tip.company':
    'Set your payroll close day in Company Settings to automate salary cycle deadlines.',
  'support.tip.dashboard':
    'Use the salary calculator widget on the dashboard to estimate net pay before processing.',
  'support.priority.low': 'Low',
  'support.priority.medium': 'Medium',
  'support.priority.high': 'High',
  'support.priority.critical': 'Critical',
  'support.modules.dashboard': 'Dashboard',
  'support.modules.hourlyPayments': 'Hourly Payments',
  'support.modules.fixedSalary': 'Fixed Salary',
  'support.modules.timeTracking': 'Time Tracking',
  'support.modules.users': 'Users & Teams',
  'support.modules.integrations': 'Integrations',
  'support.modules.profile': 'Profile',
  'support.modules.history': 'History',
  'support.modules.company': 'Company Settings',
  'support.modules.other': 'Other',
  'support.bug.title': 'Report a Bug',
  'support.bug.priority': 'Priority',
  'support.bug.module': 'Affected module',
  'support.bug.subject': 'Subject',
  'support.bug.subjectPlaceholder': 'Brief summary of the issue',
  'support.bug.description': 'Description',
  'support.bug.descriptionPlaceholder': 'What happened? What did you expect to happen?',
  'support.bug.screenshot': 'Screenshot',
  'support.bug.screenshotAlt': 'Screenshot',
  'support.bug.capturing': 'Capturing…',
  'support.bug.capturingScreenshot': 'Capturing screenshot…',
  'support.bug.capturingOverlay': 'Capturing screenshot…',
  'support.bug.retake': 'Retake',
  'support.bug.remove': 'Remove',
  'support.bug.captureScreenshot': 'Capture Screenshot',
  'support.bug.send': 'Send Report',
  'support.bug.toast.sent': 'Bug report sent!',
  'support.bug.toast.captureFailed': 'Could not capture screenshot',
  'support.contact.title': 'Feedback',
  'support.contact.subject': 'Subject',
  'support.contact.subjectPlaceholder': 'What is your message about?',
  'support.contact.message': 'Message',
  'support.contact.messagePlaceholder': 'What could be improved? What did you like or dislike?',
  'support.contact.deflection': 'These articles might help',
  'support.contact.answered': 'This answered my question',
  'support.contact.notWanted': 'Not what you were looking for? Continue below.',
  'support.contact.send': 'Send Message',
  'support.contact.resolvedTitle': 'Glad we could help!',
  'support.contact.resolvedSubtitle': 'Let us know if you need anything else.',
  'support.contact.toast.sent': 'Feedback sent! Thanks for helping us improve.',
  'support.faq.title': 'Frequently Asked Questions',
  'support.faq.searchPlaceholder': 'Search questions…',
  'support.faq.categoryAll': 'All',
  'support.faq.noResults': 'No results found',
  'support.faq.noResultsHint': 'Try different keywords or browse all categories',
  'support.faq.askDirectly': 'Ask us directly',
  'support.faq.bottomPrompt': "Didn't find what you were looking for?",
  'support.faq.leaveFeedback': 'Leave Feedback',
  'support.faq.cat.general': 'General',
  'support.faq.cat.payments': 'Payments',
  'support.faq.cat.employees': 'Employees',
  'support.faq.cat.timeTracking': 'Time Tracking',
  'support.faq.cat.integrations': 'Integrations',
  'support.faq.cat.account': 'Account & Security',
  'support.faq.general.whatIsCeleri.q': 'What is Celeri?',
  'support.faq.general.whatIsCeleri.a':
    'Celeri is a real-time payroll and salary management platform designed for companies and their employees. It allows employers to process hourly and fixed salary payments, manage employees, integrate with accounting systems, and handle domestic and international transfers.',
  'support.faq.general.whoCanUse.q': 'Who can use Celeri?',
  'support.faq.general.whoCanUse.a':
    'Celeri is built for two types of users: Employers (admins who manage payroll, users, and company settings) and Employees/Contractors (who submit time entries, view their salary, and manage their profile).',
  'support.faq.general.whichCountries.q': 'Which countries does Celeri support?',
  'support.faq.general.whichCountries.a':
    'Celeri is primarily designed for Norwegian companies but supports international payments across Europe including Sweden, Denmark, Finland, Germany, Austria, and many more countries via IBAN transfers.',
  'support.faq.general.mobileApp.q': 'Is Celeri available on mobile?',
  'support.faq.general.mobileApp.a':
    'Yes, Celeri is available as a web app and as a native mobile app on iOS and Android. The experience is fully responsive and optimized for all screen sizes.',
  'support.faq.general.changeLanguage.q': 'How do I change the language?',
  'support.faq.general.changeLanguage.a':
    'Click on your user avatar in the top-right corner, then select "Language" from the menu. Currently supported languages are Norwegian (Bokmål) and English.',
  'support.faq.payments.processHourlyPayment.q': 'How do I process an hourly payment?',
  'support.faq.payments.processHourlyPayment.a':
    'Go to Hourly Payments, click "Add Payment", select the employee, enter the date, hours worked, and job title. The system will automatically calculate the gross salary and tax. Once reviewed, click Approve to initiate the transfer.',
  'support.faq.payments.fixedSalaryCycle.q': 'How do I set up a fixed salary cycle?',
  'support.faq.payments.fixedSalaryCycle.a':
    'Go to Fixed Salary and create a new cycle. You can configure the frequency (daily, weekly, monthly), the employee, employment type, and the salary amount. The system will process payments automatically at each interval.',
  'support.faq.payments.paymentStatuses.q': 'What payment statuses exist?',
  'support.faq.payments.paymentStatuses.a':
    'Payments go through the following statuses: Pending → In Progress → Approved / Rejected. Failed payments are flagged with an "Action Required" status so you can retry or investigate the issue.',
  'support.faq.payments.internationalPayments.q': 'Can I send international payments?',
  'support.faq.payments.internationalPayments.a':
    'Yes. Celeri supports both domestic BBAN (Norwegian account numbers) and international IBAN payments with SWIFT/BIC codes. You can add international bank accounts in the employee profile or during payment setup.',
  'support.faq.payments.paymentDuration.q': 'How long does a payment take?',
  'support.faq.payments.paymentDuration.a':
    'Domestic Norwegian transfers typically arrive the same business day or within 1 business day. International IBAN transfers may take 1–3 business days depending on the destination country and bank.',
  'support.faq.payments.actionRequired.q': 'Why is my payment showing "Action Required"?',
  'support.faq.payments.actionRequired.a':
    'This usually means there is missing information — such as the employee lacking a valid bank account, an expired bank consent, or a verification issue with ZTL. Click on the payment to see the specific reason and follow the steps to resolve it.',
  'support.faq.employees.addEmployee.q': 'How do I add a new employee?',
  'support.faq.employees.addEmployee.a':
    'Go to Users and click "Add User". You can add employees manually by entering their name, email, and employment details, or import them in bulk from your accounting system (Tripletex, Systima, Unimicro, DNB) or via CSV.',
  'support.faq.employees.readyForPayroll.q': 'What does "Ready for Payroll" mean?',
  'support.faq.employees.readyForPayroll.a':
    '"Ready for Payroll" means the employee has a complete profile: national ID, birth date, bank account, and valid employment details. Employees not marked as ready cannot receive payments.',
  'support.faq.employees.employmentTypes.q': 'What employment types are supported?',
  'support.faq.employees.employmentTypes.a':
    'Celeri supports: Ordinary, Maritime, Contractor/Freelancer, and Pension employment types. Each type affects how taxes and social contributions are calculated.',
  'support.faq.employees.multipleEmployers.q': 'Can an employee have multiple employers?',
  'support.faq.employees.multipleEmployers.a':
    'Yes, employees can be linked to multiple companies in Celeri. Each employment relationship is managed separately with its own settings, salary, and history.',
  'support.faq.employees.syncFromIntegration.q':
    'How do I sync employees from my accounting system?',
  'support.faq.employees.syncFromIntegration.a':
    'Go to Company → Integrations, connect your accounting system (Tripletex, Systima, Unimicro, or DNB), then go to Users and use the "Import from Integration" option to pull employee data automatically.',
  'support.faq.timeTracking.howItWorks.q': 'How does time tracking work?',
  'support.faq.timeTracking.howItWorks.a':
    'Employees log their work hours in the Time Tracking section. Each entry generates a payment request that the employer must approve. Once approved, the hours are converted into an hourly payment.',
  'support.faq.timeTracking.overtimeThresholds.q': 'Can I set overtime thresholds?',
  'support.faq.timeTracking.overtimeThresholds.a':
    'Yes. Employers can configure overtime thresholds in Company Settings. Hours worked beyond the threshold will be flagged for different rate calculation.',
  'support.faq.timeTracking.pendingVsSent.q':
    'What is the difference between "Pending" and "Sent" time entries?',
  'support.faq.timeTracking.pendingVsSent.a':
    '"Pending" entries have been logged by the employee but not yet submitted for approval. "Sent" entries have been submitted to the employer and are awaiting review.',
  'support.faq.integrations.accountingSystems.q':
    'Which accounting systems does Celeri integrate with?',
  'support.faq.integrations.accountingSystems.a':
    'Celeri integrates with Tripletex, Systima, Unimicro, and DNB. These integrations allow you to sync employee data and bookkeeping entries automatically.',
  'support.faq.integrations.whatIsZtl.q': 'What is ZTL and why do I need it?',
  'support.faq.integrations.whatIsZtl.a':
    'ZTL (Zaptec Transfer & Liquidity) is the payment processing provider Celeri uses to execute bank transfers. You need to complete ZTL verification (KYB — Know Your Business) and grant bank consent to send payroll payments.',
  'support.faq.integrations.connectBank.q': 'How do I connect my bank account?',
  'support.faq.integrations.connectBank.a':
    'Go to Company → Integrations and click on ZTL. Follow the KYB verification steps, then add your company bank account. You will also need to grant bank consent to authorize Celeri to initiate payments.',
  'support.faq.integrations.consentExpired.q': 'What happens if my bank consent expires?',
  'support.faq.integrations.consentExpired.a':
    'If your bank consent expires, payment processing will be paused and affected payments will show as "Action Required". Go to Company → Integrations → ZTL to renew your bank consent.',
  'support.faq.account.updatePersonalInfo.q': 'How do I update my personal information?',
  'support.faq.account.updatePersonalInfo.a':
    'Go to Profile → Settings. From there you can update your personal details (name, national ID, phone, birth date) and address information.',
  'support.faq.account.changePassword.q': 'How do I change my password?',
  'support.faq.account.changePassword.a':
    'Go to Profile → Security. You will find the option to change your password there. You will need to enter your current password to set a new one.',
  'support.faq.account.pinMobile.q': 'What is the PIN used for on mobile?',
  'support.faq.account.pinMobile.a':
    'The PIN is a 4-digit code used to quickly re-authenticate when you return to the Celeri mobile app after a period of inactivity. It adds an extra layer of security without requiring your full password each time.',
  'support.faq.account.switchCompanies.q': 'How do I switch between companies?',
  'support.faq.account.switchCompanies.a':
    'Click on your user avatar in the top-right corner and select "Company" from the menu. If you are associated with multiple companies, you can switch between them from there.',
  'support.success.title': 'Message sent!',
  'support.success.subtitle': "We'll get back to you as soon as possible.",
  'support.nps.prompt': "How's your experience with Celeri?",
  'support.nps.thanks': 'Thanks for your feedback!',
  'support.nps.score.1': 'Very unhappy',
  'support.nps.score.2': 'Unhappy',
  'support.nps.score.3': 'Neutral',
  'support.nps.score.4': 'Happy',
  'support.nps.score.5': 'Very happy',

  // Auth — login
  'login.title': 'Log In',
  'login.noAccount': "Don't have an account?",
  'login.signUp': 'Sign up',
  'login.forgotPassword': 'Forgot password?',
  'login.submit': 'Log In',
  'login.submitting': 'Signing in…',
  'login.sessionExpired': 'Your session expired. Please sign in again.',

  // Auth — sign up
  'signUp.title': 'Create an account',
  'signUp.haveAccount': 'Already have an account?',
  'signUp.logIn': 'Log in',
  'signUp.fullName': 'Full Name',
  'signUp.fullNamePlaceholder': 'Full name',
  'signUp.confirmPassword': 'Confirm password',
  'signUp.submit': 'Create account',
  'signUp.submitting': 'Creating account…',
  'signUp.emailTaken': 'An account already exists for that email address.',
  'signUp.backToLogIn': 'Back to log in',
  'signUp.stepKind': 'Type',
  'signUp.stepDetails': 'Details',
  'signUp.stepAccount': 'Account',
  'signUp.kindTitle': 'Choose registration type.',
  'signUp.kindDescription': 'Register your company or sign up as a user.',
  'signUp.companyTitle': 'Your company',
  'signUp.companyDescriptionEmployer': "Enter your company's name, email and organisation number.",
  'signUp.companyDescriptionEmployee': 'Enter the token your employer shared with you.',
  'signUp.accountTitle': 'Your account',
  'signUp.accountDescription': 'Enter your details to sign up.',
  'signUp.registerCompany': 'Register a company',
  'signUp.registerCompanyDescription':
    "You will need your company's name, email and organisation number",
  'signUp.registerUser': 'Sign up as a staff',
  'signUp.registerUserDescription': 'You will need to provide a token to join a company',
  'signUp.companyTokenPlaceholder': 'Company Token',

  // Auth — password reset
  'forgot.title': 'Reset Password',
  'forgot.description':
    "Enter your email address and we'll send you a link to reset your password.",
  'forgot.field': 'Email',
  'forgot.submit': 'Send reset link',
  'forgot.submitting': 'Sending…',
  'forgot.sentTitle': 'Check Your Email',
  'forgot.sentDescription':
    'Please click the link sent to your email {cred} to verify your account. Thank you!',
  'forgot.backToLogin': 'Back to log in',
  'forgot.noEmail': "Didn't receive an email?",
  'forgot.resend': 'Resend',
  'forgot.resendSuccess': "We've sent the reset link again.",
  'reset.title': 'Reset Password',
  'reset.description': 'Enter your new password.',
  'reset.submit': 'Submit',
  'reset.submitting': 'Saving…',
  'reset.doneTitle': 'Your password is changed',
  'reset.doneDescription':
    "Your password has been successfully updated. Your account's security is our priority.",
  'reset.logIn': 'Log in',
  'reset.incompleteTitle': 'This link is incomplete',
  'reset.incompleteDescription':
    'Open the link from your email exactly as it was sent, or request a new one.',
  'reset.requestNew': 'Request a new link',
  'reset.expired': 'This reset link is no longer valid. Request a new one.',

  // Auth — email confirmation
  'login.notConfirmedTitle': 'Confirm your email before signing in',
  'login.notConfirmedDescription':
    'We sent you a confirmation link when you registered. Open it, then sign in again.',
  'confirmEmail.checkTitle': 'Check your email',
  'confirmEmail.checkDescription':
    'We sent a confirmation link to {email}. The link is valid for 48 hours.',
  'confirmEmail.noEmail': "Didn't get it? Check your spam folder or send it again.",
  'confirmEmail.resend': 'Resend email',
  'confirmEmail.resendConfirmation': 'Resend confirmation email',
  'confirmEmail.sendNew': 'Send a new link',
  'confirmEmail.sending': 'Sending…',
  'confirmEmail.resent': 'If an account needs confirming, a new link is on its way.',
  'confirmEmail.rateLimited': 'Too many requests. Wait a moment and try again.',
  'confirmEmail.confirming': 'Confirming your email…',
  'confirmEmail.confirmedTitle': 'Your email is confirmed',
  'confirmEmail.confirmedDescription': 'Your account is ready. Log in to get started.',
  'confirmEmail.alreadyTitle': 'Your email is already confirmed',
  'confirmEmail.alreadyDescription': 'This link has been used before. You can log in.',
  'confirmEmail.logIn': 'Log in',
  'confirmEmail.expiredTitle': 'This link has expired',
  'confirmEmail.expiredDescription':
    "Confirmation links are valid for 48 hours. Enter your email and we'll send you a new one.",
  'confirmEmail.invalidTitle': 'This link is not valid',
  'confirmEmail.invalidDescription':
    "The link may be incomplete or replaced by a newer one. Enter your email and we'll send you a new one.",
  'confirmEmail.failedTitle': "We couldn't confirm your email",
  'confirmEmail.failedDescription': 'Something went wrong on our side. Try again in a moment.',
  'confirmEmail.retry': 'Try again',

  // Validation — copy matches React `Global.validation` for the auth fields.
  'validation.required': 'Required.',
  'validation.invalidEmail': 'Invalid Email.',
  'validation.invalidInput': 'Invalid input.',
  'validation.minLength': 'Must be at least {number} characters.',
  'validation.maxLength': 'Must be at most {number} characters.',
  'validation.pattern': 'This value is not in the expected format',
  'validation.passwordsNotMatch': 'Passwords do not match.',
  'validation.fullName': 'Must include at least first name and last name.',
  'validation.passwordLength': 'Password must be 8+ characters.',
  'validation.passwordUppercase': 'Password needs an uppercase letter.',
  'validation.passwordDigit': 'Password needs a digit.',
  'validation.passwordSpecialCharacter': 'Password needs a special character.',

  // API error codes.
  'error.user.userNotActive.title': 'User Inactive',
  'error.user.userNotActive.message': 'User is inactive or waiting for approval.',
  'error.user.usernameInvalid': 'User does not exist in the system.',
  'error.generic.message':
    "We're sorry, but an unexpected error has occurred. Please try again later.",
  'error.auth.failedToLogin': 'Failed to login.',
  'error.auth.userInactive': 'User is inactive.',
  'error.auth.emailOrPasswordInvalid': 'Email or password is incorrect.',
  'error.auth.passwordsDoNotMatch': 'Passwords do not match.',
  'error.auth.userAlreadyExists': 'User already exists.',
  'error.auth.failedToDecodeToken': 'Failed to decode token.',
  'error.auth.failedToSaveToken': 'Failed to save token.',
  'error.auth.emailNotConfirmed': 'Confirm your email before signing in.',
  'error.auth.confirmationExpired': 'This confirmation link has expired. Request a new one.',
  'error.auth.confirmationInvalid': 'This confirmation link is not valid. Request a new one.',
  'error.company.failedToChange': 'Failed to change company.',
  'error.company.nameTooLong': 'Company name must be at most 35 characters.',
  'error.workspace.failedToSetDefault':
    'We could not open your workspace. Contact an administrator.',
  'error.record.cannotDeleteLast': 'Last sub-record cannot be deleted.',
  'error.projects.noWorkspace': 'Select a workplace to manage projects.',
  'error.projects.notFound': 'That project is no longer in this workplace.',
  'error.projects.invalid': 'Check the project fields and try again.',
  'error.projects.unavailable': 'Projects are temporarily unavailable. Try again later.',
  'error.scheduler.cannotTimeTrackDraft': 'Time cannot be tracked until the shift is published.',

  // Roles
  'role.hq_global_admin': 'Admin',
  'role.hq_admin': 'Admin',
  'role.employer': 'Employer',
  'role.employee': 'Employee',
  'role.contractor': 'Contractor',
  'role.volunteer': 'Volunteer',
  'role.personal': 'Personal',

  'dashboard.welcomeBack': 'Welcome back, {name}',
  'dashboard.welcome': 'Welcome back',
  'dashboard.description': 'A snapshot of what has been happening across your workspace.',
  'dashboard.error': 'We could not load your dashboard',
  'dashboard.keyMetrics': 'Key metrics',
  'dashboard.recentActivity': 'Recent activity',
  'dashboard.loadingActivity': 'Loading activity entry',
  'dashboard.noActivity': 'No activity yet',
  'dashboard.noActivityHint': 'Actions taken across your workspace will show up here.',
  'dashboard.stat.hoursLogged': 'Hours logged',
  'dashboard.stat.overtimeHours': 'Overtime hours',
  'dashboard.stat.overtimeShare': 'Overtime share',
  'dashboard.stat.grossPayroll': 'Gross payroll',
  'dashboard.companies': 'Companies',
  'dashboard.or': 'or',
  'dashboard.addFirstCompany.title': 'Create Your First Company!',
  'dashboard.addFirstCompany.description':
    "Welcome aboard! Let's get started by creating your first company. Take a moment to set up your company, and you'll be ready to dive into all the benefits we offer!",
  'dashboard.addFirstCompany.action': 'Create First Company',
  'dashboard.askForInvite.title': 'Ask your employer to invite you',
  'dashboard.askForInvite.description':
    'You are not associated with any employer yet. Please ask your employer to invite you to their workspace. Once invited, you will see the invitation in the notification section (bell icon in the top bar).',
  'dashboard.scheduler.hoursShort': '{value} h',
  'dashboard.scheduler.status.open': 'Open',
  'dashboard.scheduler.workLog.checkedIn': 'Checked in',
  'dashboard.scheduler.workLog.paused': 'Paused',
  'dashboard.scheduler.kpis.staffing.label': 'Staffing',
  'dashboard.scheduler.kpis.staffing.coverage': '{percent} % covered',
  'dashboard.scheduler.kpis.absences.label': 'Absences',
  'dashboard.scheduler.kpis.absences.currentMonth': 'In the current month',
  'dashboard.scheduler.kpis.openShifts.label': 'Open Shifts',
  'dashboard.scheduler.kpis.openShifts.thisWeek': 'This week',
  'dashboard.scheduler.today.title': "Today's Shifts",
  'dashboard.scheduler.today.openBadge': '{count} open',
  'dashboard.scheduler.today.empty': 'No shifts today',
  'dashboard.scheduler.openShifts.title': 'Open Shifts',
  'dashboard.scheduler.openShifts.empty': 'All shifts are filled',
  'dashboard.scheduler.openShifts.goToPlanner': 'Go to planner',
  'dashboard.scheduler.ongoing.title': 'Ongoing shifts',
  'dashboard.scheduler.ongoing.empty': 'No ongoing shifts',
  'dashboard.scheduler.bottom.scheduledHours.label': 'Scheduled Hours',
  'dashboard.scheduler.bottom.scheduledHours.sub': 'This week',
  'dashboard.scheduler.bottom.confirmed.label': 'Confirmed Shifts',
  'dashboard.scheduler.bottom.confirmed.sub': 'of {total} shifts',
  'dashboard.scheduler.bottom.unscheduled.label': 'Without Shift',
  'dashboard.scheduler.bottom.unscheduled.sub': 'employees this week',
  'dashboard.scheduler.bottom.nextWeek.label': 'Next Week',
  'dashboard.scheduler.bottom.nextWeek.nonePlanned': 'No shifts planned',
  'dashboard.scheduler.bottom.nextWeek.planned': 'shifts planned',
  'dashboard.wallet.title': 'Company wallet',
  'dashboard.wallet.status.active': 'Active',
  'dashboard.wallet.status.creating': 'Creating',
  'dashboard.wallet.status.failed': 'Failed',
  'dashboard.wallet.available': 'Available',
  'dashboard.wallet.actual': 'Actual balance',
  'dashboard.wallet.cardLabel': 'Card',
  'dashboard.wallet.cardMask': '•••• {last4}',
  'dashboard.wallet.program': 'Program',
  'dashboard.wallet.since': 'Opened',
  'dashboard.wallet.create': 'Create wallet',
  'dashboard.wallet.created': 'The company wallet is ready.',
  'dashboard.wallet.error': 'We could not load the company wallet',
  'dashboard.wallet.balanceError': 'The balance is unavailable right now.',
  'dashboard.wallet.absent.heading': 'No wallet yet',
  'dashboard.wallet.absent.description':
    'Create a company wallet to hold funds for this workspace.',
  'dashboard.wallet.creating.heading': 'Creating the wallet',
  'dashboard.wallet.creating.description':
    'This takes a moment. Refresh to see whether it is ready.',
  'dashboard.wallet.failed.heading': 'The wallet was not created',
  'dashboard.wallet.failed.description': 'The last attempt did not complete. You can try again.',
  'dashboard.myWallet.title': 'My wallet',
  'dashboard.myWallet.create': 'Create my wallet',
  'dashboard.myWallet.created': 'Your wallet is ready.',
  'dashboard.myWallet.error': 'We could not load your wallet',
  'dashboard.myWallet.absent.description':
    'Create your personal wallet to receive payments from this company.',
  'dashboard.wallet.topUp.open': 'Top up',
  'dashboard.wallet.topUp.amount': 'Amount',
  'dashboard.wallet.topUp.submit': 'Top up',
  'dashboard.wallet.topUp.invalid': 'Enter an amount above zero with at most two decimals.',
  'dashboard.wallet.topUp.confirmTitle': 'Top up the company wallet?',
  'dashboard.wallet.topUp.confirmMessage': '{amount} will be loaded onto the company wallet.',
  'dashboard.wallet.topUp.done': 'The company wallet was topped up.',
  'dashboard.payments.title': 'Payments',
  'dashboard.payments.error': 'We could not load the payments',
  'dashboard.payments.empty.heading': 'Nothing to pay',
  'dashboard.payments.empty.description': 'Approved payments appear here until they are paid.',
  'dashboard.payments.kind.hourly': 'Hourly',
  'dashboard.payments.kind.fixed': 'Salary',
  'dashboard.payments.pay': 'Pay',
  'dashboard.payments.retry': 'Try again',
  'dashboard.payments.retryCredit': 'Retry credit',
  'dashboard.payments.finish': 'Finish',
  'dashboard.payments.paid': 'Payment sent.',
  'dashboard.payments.confirmTitle': 'Pay this employee?',
  'dashboard.payments.confirmMessage': '{amount} will be paid from the company wallet to {name}.',
  'dashboard.payments.state.failed': 'Refused',
  'dashboard.payments.state.creditPending': 'Not yet credited',
  'dashboard.payments.state.paid': 'Paid',
  'dashboard.payments.state.inProgress': 'In progress',
  'dashboard.payments.state.needsReview': 'Needs review',
  'dashboard.payments.state.noWallet': 'No wallet',
  'dashboard.payments.state.currency': 'Other currency',
  'dashboard.payments.hint.creditPending':
    'The company wallet was debited. Retrying credits the employee only.',
  'dashboard.payments.hint.needsReview':
    'The provider did not confirm this payment. Check it with EML before doing anything else.',
  'dashboard.payments.hint.noWallet': 'The employee has not created a wallet yet.',
  'dashboard.payments.hint.currency':
    'This payment is in {currency}, which the wallets do not hold.',
  'activity.reportedBug': 'reported a bug',
  'activity.sentMessage': 'sent a message',
  'common.refresh': 'Refresh',
  'common.download': 'Download',
  'common.tryAgain': 'Try again',
  'common.copy': 'Copy to clipboard',
  'common.copied': 'Copied',
  'common.copyFailed': 'Could not copy to the clipboard.',

  'settings.title': 'Settings',
  'settings.description': 'Preferences that apply to your account.',
  'settings.theme': 'Theme',
  'settings.themeCurrent': 'Currently showing the {mode} palette.',
  'settings.themeLightHint': 'Always use the light palette',
  'settings.themeDarkHint': 'Always use the dark palette',
  'settings.themeSystemHint': 'Follow your operating system setting',
  'settings.emailNotifications': 'Email notifications',
  'settings.securityAlerts': 'Security alerts',
  'settings.securityAlertsHint': 'Sign-in attempts and credential changes. Always on.',
  'settings.productUpdates': 'Product updates',
  'settings.productUpdatesHint': 'Occasional notes about new capabilities.',
  'settings.weeklyDigest': 'Weekly digest',
  'settings.weeklyDigestHint': 'A Monday summary of workspace activity.',
  'settings.about': 'About',
  'settings.environment': 'Environment',
  'settings.api': 'API',
  'settings.saved': 'Preferences saved.',
  'myApps.loading': 'Loading applications…',
  'myApps.error': 'We could not load applications',
  'myApps.empty': 'No applications available',
  'myApps.rent': 'Rent',
  'myApps.configure': 'Configure',
  'myApps.enable': 'Enable',
  'myApps.disable': 'Disable',
  'myApps.cancelRent': 'Cancel rental',
  'myApps.rented': 'Application rented.',
  'myApps.enabled': 'Application enabled.',
  'myApps.disabled': 'Application disabled.',
  'myApps.cancelled': 'Rental cancelled.',
  'myApps.priceChanged': 'The price has changed. Please review and confirm.',
  'profile.title': 'My Account',
  'profile.tabInfo': 'Info',
  'profile.tabEmblems': 'Emblems',
  'profile.emblems.title': 'Emblems',
  'profile.emblems.loading': 'Loading emblems…',
  'profile.emblems.error': 'We could not load the emblems',
  'profile.emblems.empty': 'No emblems in this category',
  'profile.emblems.filter': 'Emblem categories',
  'profile.emblems.all': 'All',
  'profile.emblems.category.start': 'Start & shifts',
  'profile.emblems.category.crew': 'Crew & teamwork',
  'profile.emblems.category.skills': 'Skills & learning',
  'profile.emblems.category.live': 'Live & event',
  'profile.emblems.category.milestones': 'Milestones',
  'profile.emblems.suggestion': 'Suggested way to earn it',
  'profile.description': 'How you appear to the rest of your workspace.',
  'profile.error': 'We could not load your profile',
  'profile.loading': 'Loading profile…',
  'profile.yourRoles': 'Your roles',
  'profile.rolesHint': 'Defined in this workspace',
  'profile.identity': 'Identity',
  'profile.firstName': 'First name',
  'profile.middleName': 'Middle name',
  'profile.lastName': 'Last name',
  'profile.personalDetails': 'Personal Details',
  'profile.birthDate': 'Birth Date',
  'profile.contact': 'Contact',
  'profile.emailHint': 'Contact an administrator to change your sign-in address.',
  'profile.phone': 'Phone Number',
  'profile.phonePlaceholder': 'Phone number',
  'profile.phoneInternational': 'Phone (international)',
  'profile.language': 'Language',
  'profile.jobTitle': 'Job title',
  'profile.jobTitleHint': 'Set on your employment record, not on your profile.',
  'profile.updated': 'Profile was successfully updated.',
  'profile.deleteAccount': 'Delete Account',
  'profile.deleteAccountHeading': 'Delete Your Account',
  'profile.deleteAccountDescription':
    'Are you sure you want to delete your account? This action cannot be undone.',
  'profile.deleteAccountSuccess': 'Your account was successfully deleted.',
  'company.title': 'Company',
  'company.brand.title': 'Brand',
  'company.brand.hint':
    'Your logo and colour on the pages applicants open from your recruiting links.',
  'company.brand.error': 'The brand could not be loaded.',
  'company.brand.logo': 'Logo',
  'company.brand.uploadLogo': 'Upload logo',
  'company.brand.replaceLogo': 'Replace logo',
  'company.brand.removeLogo': 'Remove',
  'company.brand.logoHint':
    'PNG, JPG or WebP, up to 2 MB. A wide logo on a transparent background works best.',
  'company.brand.logoTooLarge': 'The logo is larger than 2 MB.',
  'company.brand.logoSaved': 'Logo saved.',
  'company.brand.logoRemoved': 'Logo removed.',
  'company.brand.color': 'Colour',
  'company.brand.colorHint':
    'Used for the main buttons; darkened if needed so their text stays readable.',
  'company.brand.colorInvalid': 'Enter a colour as #rrggbb.',
  'company.brand.colorSaved': 'Colour saved.',
  'company.brand.resetColor': "Use Celeri's colours",
  'company.employeeTitle': 'Company Settings',
  'company.settingsTitle': 'Settings',
  'company.schedulerTitle': 'Scheduler',
  'company.aiSettingsTitle': 'AI Settings',
  'company.details': 'Company Details',
  'company.name': 'Company Name',
  'company.namePlaceholder': 'Enter company name',
  'company.email': 'Company email',
  'company.emailPlaceholder': 'e.g. post@company.com',
  'company.code': 'Company code (organisation number)',
  'company.codePlaceholder': 'e.g. 923456789',
  'company.address.title': 'Address',
  'company.address.street': 'Street',
  'company.address.house': 'House number',
  'company.address.postCode': 'Post code',
  'company.address.city': 'City',
  'company.address.country': 'Country',
  'company.address.countryHint': 'Search by name or code, e.g. Norway or NO.',
  'company.address.countryInvalid': 'Choose a country from the list.',
  'company.address.countryPlaceholder': 'Search for a country',
  'company.address.province': 'State / province',
  'company.address.provinceHint':
    'ISO 3166-2 region code, e.g. 03 for Oslo or BY for Bavaria. Required for wallet top-ups and payments.',
  'company.address.provinceInvalid': 'Use the ISO 3166-2 region code, e.g. 03 or NO-03.',
  'company.addCompanyTitle': 'Add New Company',
  'company.addCompanyHint': 'You can add the address and other details later in Company Settings.',
  'company.addCompanySuccess': 'Company was successfully created.',
  'company.updated': 'Company settings were successfully updated.',
  'company.error': 'We could not load the company',
  'company.loading': 'Loading company…',
  'company.notLoaded': 'Company has not loaded yet.',
  'company.noCompany': 'No company selected',
  'company.token': 'Company Token',
  'company.tokenDescription':
    'By using this token, users can register in Celeri as employees of your company.',
  'company.tokenCopyFailed': 'Could not copy the token.',
  'company.companySettings': 'Company Settings',
  'company.overtime': 'Activate Overtime',
  'company.overtimeHint': 'Activate if your company pays overtime.',
  'company.overtimeThreshold': 'Overtime Daily Threshold',
  'company.overtimeThresholdHint': 'Hours before overtime applies.',
  'company.overtimeThresholdTooSmall': 'Must be bigger than 0.',
  'company.overtimeThresholdTooBig': 'Cannot be bigger than 24.',
  'company.doubleBooking': 'Double booking',
  'company.doubleBookingEnable': 'Enable double booking',
  'company.doubleBookingHint': 'Allow employees to be booked on overlapping shifts.',
  'company.standardShift': 'Standard shift',
  'company.startTime': 'Start time',
  'company.endTime': 'End time',
  'company.workplace': 'Workplace and time registration',
  'company.workAddress': 'Work address',
  'company.timeRegistration': 'Time registration',
  'company.timeRegistrationHint': 'Employees check in and out of shifts.',
  'company.requireGps': 'Require GPS check-in',
  'company.requireGpsHint': 'Employees must be within GPS radius to check in.',
  'company.gpsRadius': 'GPS radius',
  'company.gpsRadiusHint': 'Max distance from workplace for check-in.',
  'company.checkInWindow': 'Check-in time window',
  'company.checkInWindowHint': 'Minutes before shift start check-in opens.',
  'company.aiSection': 'AI settings',
  'company.aiInfo':
    'AI uses these settings when suggesting shifts. Changes take effect from the next run.',
  'company.aiEnable': 'Enable AI suggestions',
  'company.aiEnableHint': 'Allow AI to suggest shift allocation',
  'company.aiGoal': 'Optimization goal',
  'company.aiGoalHint': 'Choose what AI should prioritize',
  'company.aiGoalCost': 'Minimize cost',
  'company.aiGoalCostHint': 'Prioritize the lowest possible wage cost.',
  'company.aiGoalBalanced': 'Balanced',
  'company.aiGoalBalancedHint': 'Balanced between cost, coverage, and satisfaction.',
  'company.aiGoalSatisfaction': 'Employee satisfaction',
  'company.aiGoalSatisfactionHint': 'Prioritize preferences and availability.',
  'company.aiCost': 'Cost',
  'company.aiSatisfaction': 'Satisfaction',
  'company.aiConstraints': 'Constraints',
  'company.aiConstraintsHint': 'Rules AI must follow',
  'company.aiRespectAvailability': 'Respect availability',
  'company.aiRespectAvailabilityHint': 'Do not assign shifts when an employee is unavailable.',
  'company.aiMinRest': 'Require min. rest time',
  'company.aiMinRestHint': 'Enforce rest-time requirements between shifts.',
  'company.aiMaxSuggestions': 'Max number of suggestions',
  'company.aiMaxSuggestionsHint': 'Number of options AI shows per run',
  'company.aiFairDistribution': 'Fair distribution',
  'company.aiFairDistributionHint': 'Distribute shifts evenly across employees.',
  'company.aiAllowOvertime': 'Allow overtime',
  'company.aiAllowOvertimeHint': 'Allow employees to work more hours than normal.',
  'company.aiOpeningHours': 'Opening hours',
  'company.aiOpeningHoursHint': 'When the business is open (used by AI)',
  'company.aiReset': 'Reset',
  'company.aiClosed': 'Closed',
  'company.aiShiftSlots': 'Shift times',
  'company.aiShiftSlotsHint': 'Standard shift types AI can use',
  'company.aiShiftName': 'Name',
  'company.aiMinBreak': 'min break',
  'company.aiMinStaff': 'min staff.',
  'company.aiAddShift': 'Add shift time',
  'company.aiNewShiftName': 'Shift time',
  'company.dayMon': 'Monday',
  'company.dayTue': 'Tuesday',
  'company.dayWed': 'Wednesday',
  'company.dayThu': 'Thursday',
  'company.dayFri': 'Friday',
  'company.daySat': 'Saturday',
  'company.daySun': 'Sunday',
  'units.hours': 'h',
  'units.meters': 'm',
  'units.minutes': 'min',
  'common.empty': '–',
  'common.save': 'Save Changes',
  'common.saving': 'Saving…',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.next': 'Next',
  'common.back': 'Back',
  'common.confirm': 'Confirm',
  'common.add': 'Add',
  'common.delete': 'Delete',
  'common.moreActions': 'More actions',
  'common.edit': 'Edit',
  'common.loading': 'Loading…',
  'table.show': 'Show',
  'table.perPage': 'per page',
  'table.empty': 'No data to display.',
  'table.selectAll': 'Select all rows',
  'table.selectRow': 'Select row',
  'table.previousPage': 'Previous page',
  'table.nextPage': 'Next page',
  'table.range': '{start} – {end} of {total}',
  'field.optional': '(Optional)',
  'field.moreInfo': 'More information',
  'search.placeholder': 'Search...',
  'search.clear': 'Clear search',
  'country.search': 'Search...',
  'country.noItemsFound': 'No items found.',
  'country.austria': 'Austria',
  'country.azerbaijan': 'Azerbaijan',
  'country.belgium': 'Belgium',
  'country.bulgaria': 'Bulgaria',
  'country.croatia': 'Croatia',
  'country.cyprus': 'Cyprus',
  'country.czechRepublic': 'Czech Republic',
  'country.denmark': 'Denmark',
  'country.estonia': 'Estonia',
  'country.finland': 'Finland',
  'country.france': 'France',
  'country.germany': 'Germany',
  'country.greece': 'Greece',
  'country.hungary': 'Hungary',
  'country.iceland': 'Iceland',
  'country.ireland': 'Ireland',
  'country.italy': 'Italy',
  'country.latvia': 'Latvia',
  'country.liechtenstein': 'Liechtenstein',
  'country.lithuania': 'Lithuania',
  'country.luxembourg': 'Luxembourg',
  'country.malta': 'Malta',
  'country.netherlands': 'Netherlands',
  'country.norway': 'Norway',
  'country.poland': 'Poland',
  'country.portugal': 'Portugal',
  'country.romania': 'Romania',
  'country.slovakia': 'Slovakia',
  'country.slovenia': 'Slovenia',
  'country.spain': 'Spain',
  'country.sweden': 'Sweden',
  'country.greatBritain': 'Great Britain',

  'users.title': 'Users',
  'users.description': 'Everyone with access to your workspace.',
  'users.error': 'We could not load users',
  'users.emptyFiltered': 'No users match those filters',
  'users.emptyFilteredHint': 'Try a different search term, or clear the filters.',
  'users.emptyHint': 'Nobody has been added to this workspace yet.',
  'users.search': 'Search users',
  'users.status': 'Status',
  'users.allStatuses': 'All statuses',
  'users.perPage': 'Per page',
  'users.range': '{first}–{last} of {total} users',
  'users.rangeOne': '{first}–{last} of {total} user',
  'users.none': '0 users',
  'users.tableCaption': 'Users in your workspace, newest first',
  'users.colUser': 'User',
  'users.colRoles': 'Roles',
  'users.colStatus': 'Status',
  'users.payrollActionsFor': 'Payroll actions for {name}',
  'users.payrollProfile': 'Payroll profile',
  'users.payrollFixProfile': 'Fix payroll profile ({count} problems)',
  'users.colPayroll': 'Payroll (PoP)',
  'users.colTeams': 'Teams',
  'users.colAdded': 'Added',
  'users.colActions': 'Actions',
  'users.actionsFor': 'Actions for {name}',
  'users.viewDetails': 'View details',
  'users.remove': 'Remove',
  'users.removeTitle': 'Remove user',
  'users.removeMessage': '{name} ({email}) will lose access immediately.',
  'users.updated': '{name} was updated.',
  'users.removed': '{name} was removed.',
  'users.detailsTitle': 'User details',
  'users.loading': 'Loading user…',
  'users.emailReadonly': 'The sign-in address cannot be changed here.',
  'users.tabUsers': 'Users',
  'users.tabTeams': 'Teams',
  'users.addUser': 'Add user',
  'users.addManually': 'Provide manually',
  'users.uploadCsv': 'Upload CSV file',
  'users.csvTitle': 'Contact us to upload CSV file',
  'users.csvDescription':
    'Send us a CSV or spreadsheet and we will import users from the file to the system.',
  'users.csvSendEmail': 'Open email client',
  'users.addTitle': 'Add User',
  'users.addEmailHint':
    "To get started, please enter the email address. We'll check if the user already have an account in our system.",
  'users.addExistingNotice':
    'This user already exists in the system and will be added to the company.',
  'users.addRoleHint':
    'Select the role for the user. This will determine their permissions and access within the system.',
  'users.created': 'User was successfully created.',
  'users.fullName': 'Full name',
  'users.email': 'Email',
  'users.phone': 'Phone Number',
  'users.tags': 'Tags',
  'users.addTag': 'Add tag',
  'users.tabPersonal': 'Personal',
  'users.tabRole': 'Role',
  'users.roleEmployee': 'Employee',
  'users.roleEmployeeHint': 'Can track their work and ask for payments.',
  'users.roleEmployer': 'Employer',
  'users.roleEmployerHint': 'Can manage the company, users, and payment requests.',
  'users.roleVolunteer': 'Volunteer',
  'users.roleVolunteerHint': 'Can track their work.',
  'users.roleContractor': 'Contractor',
  'users.roleContractorHint': 'Can track their work and ask for payments.',
  'users.editTitle': 'Edit User',
  'users.activityTitle': 'User Activity',
  'users.activityAction': 'Action',
  'users.activityCreated': 'Date',
  'users.activityBy': 'Initiated by',
  'users.activityResend': 'Resend notification',
  'users.activityResendSuccess': 'Notification was sent',
  'users.sendNotification.title': 'Send notification',
  'users.sendNotification.messageLabel': 'Message',
  'users.sendNotification.addMessage': 'Add message',
  'users.sendNotification.editMessage': 'Edit message',
  'users.sendNotification.confirm': 'Send',
  'users.sendNotification.success': 'Notification sent successfully.',
  'users.sendNotification.editorTitle': 'Compose message',
  'users.sendNotification.editorConfirm': 'Confirm',
  'users.sendNotification.recipientTypeLabel': 'Send to',
  'users.sendNotification.type.users': 'Users',
  'users.sendNotification.type.teams': 'Teams',
  'users.sendNotification.type.roles': 'Roles',
  'users.sendNotification.slots.empty': 'No recipients added yet',
  'users.sendNotification.slots.labelUsers': 'Users',
  'users.sendNotification.slots.labelTeams': 'Teams',
  'users.sendNotification.slots.labelRoles': 'Roles',
  'users.sendNotification.slots.addUsers': 'Add users',
  'users.sendNotification.slots.addTeams': 'Add teams',
  'users.sendNotification.slots.addRoles': 'Add roles',
  'users.sendNotification.drawer.titleUsers': 'Add users',
  'users.sendNotification.drawer.titleTeams': 'Add teams',
  'users.sendNotification.drawer.titleRoles': 'Add roles',
  'users.sendNotification.removeRecipient': 'Remove {name}',
  'users.activity.message_sent': 'Notification sent',
  'users.activity.login': 'Login',
  'users.activity.logout': 'Logout',
  'users.activity.profile_updated': 'Profile updated',
  'users.activity.password_changed': 'Password changed',
  'users.activity.employee_updated': 'Employee updated by employer',
  'users.activityStatus.failed': 'Failed',
  'users.activityStatus.sent': 'Sent',
  'users.activityStatus.queued': 'Queued',
  'users.activityStatus.logged': 'Logged',
  'users.countSelected': '{count} selected',
  'users.deleteTitle': 'Delete User',
  'users.deleteMessage': 'Are you sure you want to delete {name}? This action cannot be undone.',
  'users.deleteBulkTitle': 'Delete Users',
  'users.deleteBulkMessage': 'Are you sure you want to delete selected users?',
  'users.deleted': 'User was successfully deleted.',
  'users.needRole': 'Select at least one role.',
  'users.teams.add': 'Add team',
  'users.teams.colName': 'Name',
  'users.teams.workSummary': 'Work Summary',
  'users.teams.addTitle': 'Add team',
  'users.teams.nameLabel': 'Team name',
  'users.teams.namePlaceholder': 'Enter team name',
  'users.teams.created': 'Team created.',
  'users.teams.deleteTitle': 'Delete team',
  'users.teams.deleteMessage': 'Are you sure you want to delete {name}?',
  'users.teams.deleted': 'Team deleted successfully.',
  'users.teams.membersTitle': 'Team members - {name}',
  'users.teams.addMember': 'Add user',
  'users.teams.removeMember': 'Remove user',
  'users.teams.leader': 'Leader',
  'users.teams.markLeader': 'Mark as leader',
  'users.teams.unmarkLeader': 'Unmark as leader',
  'users.teams.selectUser': 'Select a user',
  'users.teams.workSummaryTitle': 'Work Summary - {name}',
  'users.teams.dateRange': 'Date range',
  'users.teams.startDate': 'Start date',
  'users.teams.endDate': 'End date',
  'users.teams.totalAmount': 'Total amount',
  'users.teams.shiftDate': 'Shift date',
  'users.teams.planned': 'Planned',
  'users.teams.timeWorked': 'Time worked',
  'users.teams.amount': 'Amount',
  'users.teams.download': 'Download',
  'users.teams.error': 'We could not load teams',
  'users.teams.empty': 'No teams yet.',
  'users.tabPositions': 'Positions',
  'users.position': 'Position',
  'users.positions.add': 'Add position',
  'users.positions.addTitle': 'Add position',
  'users.positions.editTitle': 'Edit position',
  'users.positions.colName': 'Name',
  'users.positions.colDescription': 'Description',
  'users.positions.colPeople': 'People',
  'users.positions.nameLabel': 'Position name',
  'users.positions.namePlaceholder': 'For example Bartender',
  'users.positions.descriptionLabel': 'Description',
  'users.positions.descriptionPlaceholder': 'What the job involves',
  'users.positions.created': 'Position created.',
  'users.positions.updated': 'Position saved.',
  'users.positions.deleted': 'Position deleted.',
  'users.positions.deleteTitle': 'Delete position',
  'users.positions.deleteMessage': 'Are you sure you want to delete {name}?',
  'users.positions.deleteMessageHeld':
    'Are you sure you want to delete {name}? People with this position: {count}. They will have no position.',
  'users.positions.error': 'We could not load positions',
  'users.positions.empty': 'No positions yet.',
  'users.positions.emptyHint': 'Add the jobs people do here, then give each person one.',
  'users.positions.all': 'All positions',
  'users.positions.none': 'No position',
  'users.positions.nameTaken': 'A position with this name already exists.',
  'users.positions.tooLong': 'Use at most {max} characters.',
  'users.positions.notFound': 'That position no longer exists. Refresh and try again.',
  'users.positions.invalid': 'Check the position name and description.',
  'status.active': 'Active',
  'status.pending_approval': 'Pending approval',
  'status.inactive': 'Inactive',
  'status.rejected': 'Rejected',
  'status.notSet': 'Not set',
  'status.unknown': 'Unknown',

  'sched.absenceRemoved': 'Absence removed.',
  'sched.absences': 'Absences',
  'sched.actions': 'Actions',
  'sched.addARoleToSayWhatWorkThis':
    'Add a role to say what work this group needs, and how many people it takes.',
  'sched.addAStarterSet': 'Add a starter set',
  'sched.addDefaults': 'Add defaults',
  'sched.addJobTitle': 'Add job title',
  'sched.addLocation': 'Add location',
  'sched.addRole': 'Add role',
  'sched.addTemplate': 'Add template',
  'sched.addTheRolesYouScheduleForSoA':
    'Add the roles you schedule for so a shift can say what work it is, and what it pays.',
  'sched.afterWhichACheckInCountsAsLate': 'After which a check-in counts as late.',
  'sched.allGroups': 'All groups',
  'sched.allPeople': 'All people',
  'sched.allRolesRemoved': 'All roles removed.',
  'sched.allowCheckIn': 'Allow check-in',
  'sched.anAbsenceCoversTheWholeDayRegisterOne':
    'An absence covers the whole day. Register one per day off.',
  'sched.anAmountPerHourForExample245Or':
    'An amount per hour, for example 245 or 245.50. Required.',
  'sched.anUnassignedShiftIsAlwaysOpen': 'An unassigned shift is always open.',
  'sched.aCheckInIsRefusedUnlessTheDevice':
    'A check-in is refused unless the device reports a position within the radius below.',
  'sched.aManagerHasToApproveTheSwapApproving':
    'A manager has to approve the swap. Approving it moves both shifts at once.',
  'sched.aManagerHasToApproveThisBeforeIt':
    'A manager has to approve this before it counts towards pay.',
  'sched.approve': 'Approve',
  'sched.approveWorkedShifts': 'Approve worked shifts',
  'sched.assignedTo': 'Assigned to',
  'sched.availability': 'Availability',
  'sched.availabilityIsGuidanceForWhoeverPlansTheWeek':
    'Availability is guidance for whoever plans the week. It does not stop a shift being created.',
  'sched.availabilityRemoved': 'Availability removed.',
  'sched.availabilitySaved': 'Availability saved.',
  'sched.availabilityUpdated': 'Availability updated.',
  'sched.back': 'Back',
  'sched.break': 'Break',
  'sched.breakMinutes': 'Break (minutes)',
  'sched.bulkActions': 'Bulk actions',
  'sched.cancel': 'Cancel',
  'sched.checkInIsSwitchedOffForThisWorkspace': 'Check-in is switched off for this workspace.',
  'sched.checkInSettings': 'Check-in settings',
  'sched.checkInSettingsSaved': 'Check-in settings saved.',
  'sched.claim': 'Claim',
  'sched.claimThisShift': 'Claim this shift',
  'sched.claimedBy': 'Claimed by',
  'sched.claimsOnOpenShiftsTheOnesWaitingOn':
    'Claims on open shifts, the ones waiting on a decision first',
  'sched.clear': 'Clear',
  'sched.clearFilters': 'Clear filters',
  'sched.clearSelection': 'Clear selection',
  'sched.clearTheRecord': 'Clear the record',
  'sched.clock': 'Clock',
  'sched.clockIn': 'Clock in',
  'sched.clockOut': 'Clock out',
  'sched.clockRecordsForTheVisibleWeekNewestFirst':
    'Clock records for the visible week, newest first',
  'sched.clockedIn': 'Clocked in',
  'sched.clockedInOnTheirBehalf': 'Clocked in on their behalf.',
  'sched.clockedIn2': 'Clocked in.',
  'sched.clockedOut': 'Clocked out',
  'sched.clockedOutOnTheirBehalf': 'Clocked out on their behalf.',
  'sched.clockedOut2': 'Clocked out.',
  'sched.consecutiveDays': 'Consecutive days',
  'sched.date': 'Date',
  'sched.daysAbsent': 'Days absent',
  'sched.daysAndEventsWithTheRolesTheyNeed':
    'Days and events, with the roles they need staffed underneath them.',
  'sched.daysPeopleWereOffAndHowTheSick':
    'Days people were off, and how the sick days add up over the year.',
  'sched.decline': 'Decline',
  'sched.default': 'Default',
  'sched.defaultJobTitleSet': 'Default job title set.',
  'sched.defaultTemplatesAdded': 'Default templates added.',
  'sched.delete': 'Delete',
  'sched.deleteGroup': 'Delete group',
  'sched.deleteJobTitle': 'Delete job title',
  'sched.deleteLocation': 'Delete location',
  'sched.deleteShifts': 'Delete shifts',
  'sched.deleteTemplate': 'Delete template',
  'sched.deleteTimeEntry': 'Delete time entry',
  'sched.doubleBooked': 'Double booked',
  'sched.downloadACsv': 'Download a CSV',
  'sched.earlyWindowMinutes': 'Early window (minutes)',
  'sched.edit': 'Edit',
  'sched.editGroup': 'Edit group',
  'sched.ends': 'Ends',
  'sched.exportAsCsv': 'Export as CSV',
  'sched.exportDownloaded': 'Export downloaded.',
  'sched.extra': 'Extra',
  'sched.extraTimeMinutes': 'Extra time (minutes)',
  'sched.filled': 'Filled',
  'sched.from': 'From',
  'sched.givesUp': 'Gives up',
  'sched.gracePeriodMinutes': 'Grace period (minutes)',
  'sched.group': 'Group',
  'sched.groupCreated': 'Group created.',
  'sched.groupDeleted': 'Group deleted.',
  'sched.groupUpdated': 'Group updated.',
  'sched.groups': 'Groups',
  'sched.hourlyRate': 'Hourly rate',
  'sched.hours': 'Hours',
  'sched.hoursAreRoundedToOneDecimalLatenessIs':
    'Hours are rounded to one decimal. Lateness is counted in minutes.',
  'sched.howLongBeforeTheShiftStarts': 'How long before the shift starts.',
  'sched.in': 'In',
  'sched.job': 'Job',
  'sched.jobTitle': 'Job title',
  'sched.jobTitleAdded': 'Job title added.',
  'sched.jobTitleDeleted': 'Job title deleted.',
  'sched.jobTitleUpdated': 'Job title updated.',
  'sched.jobTitles': 'Job titles',
  'sched.jobTitlesInThisWorkspaceTheDefaultOne':
    'Job titles in this workspace, the default one first',
  'sched.kind': 'Kind',
  'sched.last30Days': 'Last 30 days',
  'sched.late': 'Late',
  'sched.loadingGroup': 'Loading group…',
  'sched.location': 'Location',
  'sched.locationAdded': 'Location added.',
  'sched.locationDeleted': 'Location deleted.',
  'sched.locationUpdated': 'Location updated.',
  'sched.locations': 'Locations',
  'sched.locationsInThisWorkspace': 'Locations in this workspace',
  'sched.longShift': 'Long shift',
  'sched.makeDefault': 'Make default',
  'sched.message': 'Message',
  'sched.month': 'Month',
  'sched.myShifts': 'My shifts',
  'sched.name': 'Name',
  'sched.newGroup': 'New group',
  'sched.nextWeek': 'Next week',
  'sched.noAbsencesRecorded': 'No absences recorded',
  'sched.noGroupsYet': 'No groups yet',
  'sched.noJobTitlesYet': 'No job titles yet',
  'sched.noLocationsYet': 'No locations yet',
  'sched.noRolesYet': 'No roles yet',
  'sched.noShiftsFallBetweenThoseTwoDatesTry':
    'No shifts fall between those two dates. Try a wider range.',
  'sched.noShiftsInThisWeek': 'No shifts in this week.',
  'sched.noShiftsWereScheduledAndNothingWasClocked':
    'No shifts were scheduled and nothing was clocked in the week you are looking at.',
  'sched.noTemplatesYet': 'No templates yet',
  'sched.nobodyToScheduleYet': 'Nobody to schedule yet',
  'sched.nobodyToShow': 'Nobody to show',
  'sched.none': 'None',
  'sched.normal': 'Normal',
  'sched.notClocked': 'Not clocked',
  'sched.note': 'Note',
  'sched.notes': 'Notes',
  'sched.nothingHere': 'Nothing here',
  'sched.nothingInThisRange': 'Nothing in this range',
  'sched.nothingThisWeek': 'Nothing this week',
  'sched.nothingToDecide': 'Nothing to decide',
  'sched.nothingWasClockedInThisWeek': 'Nothing was clocked in this week.',
  'sched.offerASwap': 'Offer a swap',
  'sched.onABreak': 'On a break',
  'sched.oncePeopleAreAddedToThisWorkspaceThey':
    'Once people are added to this workspace they appear here, ready to say when they can work.',
  'sched.oncePeopleAreAddedToThisWorkspaceThey2':
    'Once people are added to this workspace they will appear here, ready to be given shifts.',
  'sched.openShifts': 'Open shifts',
  'sched.openShiftsYouCanTake': 'Open shifts you can take',
  'sched.out': 'Out',
  'sched.overtime': 'Overtime',
  'sched.overtimeRequested': 'Overtime requested.',
  'sched.overtimeRequestsTheOnesWaitingOnADecision':
    'Overtime requests, the ones waiting on a decision first',
  'sched.peopleCanClockInAndOutOfTheir': 'People can clock in and out of their own shifts.',
  'sched.peopleNeeded': 'People needed',
  'sched.person': 'Person',
  'sched.planTheWeekShiftByShift': 'Plan the week, shift by shift.',
  'sched.planned': 'Planned',
  'sched.plannedAgainstWorkedHoursPerPersonForA':
    'Planned against worked hours, per person, for a range you choose.',
  'sched.presetRanges': 'Preset ranges',
  'sched.previousWeek': 'Previous week',
  'sched.publish': 'Publish',
  'sched.publishGroup': 'Publish group',
  'sched.publishGroups': 'Publish groups',
  'sched.publishSchedule': 'Publish schedule',
  'sched.radiusMetres': 'Radius (metres)',
  'sched.reason': 'Reason',
  'sched.recordTime': 'Record time',
  'sched.recordWorkedTime': 'Record worked time',
  'sched.recordWorkedTime2': 'Record worked time…',
  'sched.refresh': 'Refresh',
  'sched.register': 'Register',
  'sched.registerAbsence': 'Register absence',
  'sched.registeredAbsencesNewestFirst': 'Registered absences, newest first',
  'sched.registeredBy': 'Registered by',
  'sched.remove': 'Remove',
  'sched.removeAbsence': 'Remove absence',
  'sched.removeAll': 'Remove all',
  'sched.removeAvailability': 'Remove availability',
  'sched.removeEveryRole': 'Remove every role',
  'sched.removeRole': 'Remove role',
  'sched.removeShift': 'Remove shift',
  'sched.rename': 'Rename',
  'sched.reports': 'Reports',
  'sched.requestOvertime': 'Request overtime',
  'sched.requestedBy': 'Requested by',
  'sched.requests': 'Requests',
  'sched.requireThemToBeOnSite': 'Require them to be on site',
  'sched.role': 'Role',
  'sched.roleAdded': 'Role added.',
  'sched.roleRemoved': 'Role removed.',
  'sched.roleUpdated': 'Role updated.',
  'sched.roles': 'Roles',
  'sched.rolesInThisGroupAndHowManyOf':
    'Roles in this group and how many of their slots are filled',
  'sched.saveSettings': 'Save settings',
  'sched.savedDefaultsThatFillTheShiftEditorIn':
    'Saved defaults that fill the shift editor in one click.',
  'sched.schedule': 'Schedule',
  'sched.schedulerGroupsTheMostRecentDateFirst': 'Scheduler groups, the most recent date first',
  'sched.schedulerSections': 'Scheduler sections',
  'sched.search': 'Search',
  'sched.select': 'Select',
  'sched.sendRequest': 'Send request',
  'sched.shift': 'Shift',
  'sched.shiftClaimedWaitingOnApproval': 'Shift claimed. Waiting on approval.',
  'sched.shiftRemoved': 'Shift removed.',
  'sched.shiftSwapRequestsTheOnesWaitingOnA':
    'Shift swap requests, the ones waiting on a decision first',
  'sched.shiftSwapsClaimsOnOpenShiftsAndOvertime':
    'Shift swaps, claims on open shifts, and overtime waiting to be signed off.',
  'sched.shiftTemplates': 'Shift templates',
  'sched.shiftTemplatesInThisWorkspace': 'Shift templates in this workspace',
  'sched.shiftTitle': 'Shift title',
  'sched.shiftsForTheSelectedWeekByPersonAnd': 'Shifts for the selected week, by person and day',
  'sched.shiftsInTheVisibleWeekAndWhatThe':
    'Shifts in the visible week and what the clock recorded for each',
  'sched.shiftsRecordTheirLocationAsTextSoRenaming':
    'Shifts record their location as text, so renaming this changes what the picker offers without touching shifts that already name the old one.',
  'sched.shiftsThisWeek': 'Shifts this week',
  'sched.shiftsUnderARoleTakeTheirStatusFrom':
    'Shifts under a role take their status from this group, so publishing here is what confirms them — confirming one on the week grid has no lasting effect.',
  'sched.shortRest': 'Short rest',
  'sched.showing': 'Showing',
  'sched.sickDays': 'Sick days',
  'sched.source': 'Source',
  'sched.startFromATemplate': 'Start from a template',
  'sched.starts': 'Starts',
  'sched.status': 'Status',
  'sched.stillClockedIn': 'Still clocked in',
  'sched.stillOpen': 'Still open',
  'sched.swapRequested': 'Swap requested.',
  'sched.takes': 'Takes',
  'sched.template': 'Template',
  'sched.templateAdded': 'Template added.',
  'sched.templateDeleted': 'Template deleted.',
  'sched.templateName': 'Template name',
  'sched.templateUpdated': 'Template updated.',
  'sched.templates': 'Templates',
  'sched.theDateAndLocationAreCopiedOntoEach':
    'The date and location are copied onto each role when it is created. Changing them here does not move roles that already exist.',
  'sched.theEndHasToComeAfterTheStart': 'The end has to come after the start.',
  'sched.thePlacesShiftsHappenOfferedAsChoicesIn':
    'The places shifts happen, offered as choices in the shift editor.',
  'sched.theRolesAShiftCanBeStaffedWith':
    'The roles a shift can be staffed with, and what each one pays per hour.',
  'sched.thereAreNoOtherShiftsInTheNext':
    'There are no other shifts in the next few weeks to swap with. Try again once more of the schedule is published.',
  'sched.thisMonth': 'This month',
  'sched.thisPersonIsAlreadyOnAnotherShiftAt':
    'This person is already on another shift at the same time.',
  'sched.thisShiftBelongsToAGroupAndHas':
    'This shift belongs to a group and has to be moved there.',
  'sched.thisShiftHasBeenWorkedOrApprovedAnd':
    'This shift has been worked or approved and cannot be moved.',
  'sched.thisShiftWillBeRemovedFromTheSchedule': 'This shift will be removed from the schedule.',
  'sched.thisWeek': 'This week',
  'sched.thisWritesTheWholeWorkedPeriodOntoThe':
    'This writes the whole worked period onto the shift as though it had been clocked, replacing anything already recorded against it.',
  'sched.timeClock': 'Time clock',
  'sched.timeEntries': 'Time entries',
  'sched.timeEntryDeleted': 'Time entry deleted.',
  'sched.timeRegistrationIsSwitchedOffForThisWorkspac':
    'Time registration is switched off for this workspace, so shifts here are read-only.',
  'sched.title': 'Title',
  'sched.to': 'To',
  'sched.today': 'Today',
  'sched.total': 'Total',
  'sched.tryAgain': 'Try again',
  'sched.weCouldNotLoadAbsences': 'We could not load absences',
  'sched.weCouldNotLoadAvailability': 'We could not load availability',
  'sched.weCouldNotLoadGroups': 'We could not load groups',
  'sched.weCouldNotLoadJobTitles': 'We could not load job titles',
  'sched.weCouldNotLoadLocations': 'We could not load locations',
  'sched.weCouldNotLoadRequests': 'We could not load requests',
  'sched.weCouldNotLoadTemplates': 'We could not load templates',
  'sched.weCouldNotLoadTheReport': 'We could not load the report',
  'sched.weCouldNotLoadTheSchedule': 'We could not load the schedule',
  'sched.weCouldNotLoadTheTimeClock': 'We could not load the time clock',
  'sched.weCouldNotLoadThisGroup': 'We could not load this group',
  'sched.weCouldNotLoadYourShifts': 'We could not load your shifts',
  'sched.week': 'Week',
  'sched.weekOf': 'Week of',
  'sched.whatPeopleActuallyWorkedAndTheCorrectionsMad':
    'What people actually worked, and the corrections made to it.',
  'sched.whatPeopleHaveToldYouAboutTheDays':
    'What people have told you about the days they can and cannot work.',
  'sched.whatTheShiftIsCalledOnceCreatedLeft':
    'What the shift is called once created. Left blank, the template name is used.',
  'sched.when': 'When',
  'sched.whichFiguresToShow': 'Which figures to show',
  'sched.whichRequestsToShow': 'Which requests to show',
  'sched.whichShiftsToShow': 'Which shifts to show',
  'sched.why': 'Why',
  'sched.worked': 'Worked',
  'sched.workedTimeRecorded': 'Worked time recorded.',
  'sched.working': 'Working…',
  'sched.year': 'Year',
  'sched.youGiveUp': 'You give up',
  'sched.youTake': 'You take',
  'sched.yourClockRecords': 'Your clock records',
  'sched.yourOwnRotaAndTheClockThatGoes': 'Your own rota, and the clock that goes with it.',
  'sched.clockedInNow': 'clocked in now',
  'sched.lateArrivals': 'late arrivals',
  'sched.notAvailable': 'not available',
  'sched.open': 'open',
  'sched.recordedThisWeek': 'recorded this week',
  'sched.scheduled': 'scheduled',
  'sched.shifts': 'shifts',
  'sched.slots': 'slots',
  'sched.stillOpen2': 'still open',
  'sched.unpublished': 'unpublished',
  'sched.workedThisWeek': 'worked this week',

  'rule.longShift': 'Long shift',
  'rule.longShiftDetail': '{hours} hours exceeds the {limit}-hour daily limit.',
  'rule.consecutiveDays': 'Consecutive days',
  'rule.consecutiveDaysDetail': '{days} days in a row exceeds the limit of {limit}.',
  'rule.shortRest': 'Short rest',
  'rule.shortRestDetail': '{hours} hours between shifts is below the {limit}-hour minimum.',
  'rule.doubleBooked': 'Double booked',
  'rule.doubleBookedDetail': 'This person is already on another shift at the same time.',
  'rule.overtime': 'Overtime',
  'rule.overtimeDetail': '{hours} hours this week is over the {limit}-hour threshold.',

  'sched.noAbsenceMatchesFilters': 'No absence matches those filters.',
  'sched.nobodyRegisteredAbsent': 'Nobody has been registered absent in this workspace.',
  'sched.noGroupMatchesFilters': 'No group matches those filters.',
  'sched.createGroupHint':
    'Create a group for a day or an event, then add the roles it needs staffed.',
  'sched.noLocationMatchesSearch': 'No location matches that search.',
  'sched.addLocationsHint':
    'Add the places your shifts happen so they can be picked rather than typed.',
  'sched.noTemplateMatchesSearch': 'No template matches that search.',
  'sched.saveTemplatesHint':
    'Save the shifts you create over and over, then start from one instead of retyping it.',
  'sched.yourRequests': 'Your requests',

  'sched.waitingOnDecision': '{count} waiting on a decision',

  's2.planning': 'Planning',
  's2.schedule': 'Schedule',
  's2.availability': 'Availability',
  's2.timeTracking': 'Time Tracking',
  'nav.openings': 'Open positions',
  'rc.campaign.listed': 'List publicly',
  'rc.campaign.listedHint':
    'Show this campaign on Celeri’s job board and in your workspace’s open positions while it is open.',
  'rc.channel.board': 'Celeri jobs',
  'rc.board.title': 'Jobs',
  'rc.board.lead': 'Open positions from companies on Celeri. Pick one to read more and apply.',
  'rc.board.signIn': 'Sign in',
  'rc.board.error': 'We could not load the open positions.',
  'rc.board.empty': 'No open positions right now',
  'rc.board.emptyHint': 'Check back soon.',
  'rc.board.until': 'Until {date}',
  'rc.board.from': 'From {date}',
  'rc.board.notYet': 'Opens soon',
  'rc.board.apply': 'Read and apply',
  'rc.openings.title': 'Open positions',
  'rc.openings.empty': 'No open positions in this workspace',
  'rc.openings.emptyHint': 'When your employer lists a campaign, it shows up here.',
  'tk.my.take': 'Take',
  'tk.my.start': 'Start',
  'tk.my.submit': 'Submit for approval',
  'tk.my.tab.assigned': 'Assigned to me',
  'tk.my.tab.unassigned': 'Unassigned',
  'tk.my.tab.created': 'Created by me',
  'tk.my.empty.assigned': 'Nothing is assigned to you',
  'tk.my.empty.unassigned': 'No tasks are waiting for someone to take them',
  'tk.my.empty.created': 'You have not reported or created anything yet',
  'tk.my.loadError': 'We could not load your tasks.',
  'tk.my.report': 'Report incident',
  'tk.my.reportTitle': 'What happened',
  'tk.my.reportTitlePlaceholder': 'e.g. Broken glass by the bar',
  'tk.my.reportDescription': 'Details',
  'tk.my.reportSend': 'Report',
  'tk.my.reported': 'Incident reported',
  'tk.my.reportedHint': 'Your employer follows it up.',
  'tk.my.started': 'Task started',
  'tk.my.taken': 'The task is yours',
  'tk.my.submitted': 'Sent for approval',
  'tk.my.activity': 'Task activity',
  'nav.shifts': 'Shifts',
  'nav.swaps': 'Swap',
  'nav.openShifts': 'Open shifts',
  'nav.overtime': 'Overtime',
  's2.req.status.pending': 'Pending',
  's2.req.status.approved': 'Approved',
  's2.req.status.rejected': 'Rejected',
  's2.req.loadError': 'We could not load this.',
  's2.req.send': 'Send request',
  's2.req.sentHint': 'Your employer gets it and decides.',
  's2.open.title': 'Open shifts',
  's2.open.available': 'You can take',
  's2.open.empty': 'No open shifts for you right now',
  's2.open.emptyHint': 'Shifts your employer opens, in a position you hold, show up here.',
  's2.open.claim': 'Take shift',
  's2.open.claimed': 'Request sent',
  's2.open.claimedHint': 'Your employer confirms whether the shift is yours.',
  's2.open.myClaims': 'Your requests',
  's2.swap.title': 'Swap requests',
  's2.swap.request': 'Request a swap',
  's2.swap.empty': 'No swap requests yet',
  's2.swap.emptyHint':
    'Offer one of your shifts to a colleague, or to anyone, and your employer decides.',
  's2.swap.with': 'With {name}',
  's2.swap.anyone': 'Offered to anyone',
  's2.swap.myShift': 'Your shift',
  's2.swap.pickShift': 'Choose a shift',
  's2.swap.noShifts': 'You have no upcoming shifts to offer.',
  's2.swap.colleagueShift': 'Swap with',
  's2.swap.message': 'Message',
  's2.swap.messagePlaceholder': 'e.g. I have a doctor’s appointment',
  's2.swap.sent': 'Swap request sent',
  's2.ot.title': 'Overtime',
  's2.ot.request': 'Request overtime',
  's2.ot.empty': 'No overtime requests yet',
  's2.ot.emptyHint': 'Worked longer than planned? Ask for the extra time here.',
  's2.ot.shift': 'Shift',
  's2.ot.noShifts': 'You have no shifts up to today.',
  's2.ot.hours': 'Hours',
  's2.ot.minutes': 'Minutes',
  's2.ot.reason': 'Reason',
  's2.ot.reasonPlaceholder': 'e.g. stayed to close',
  's2.ot.sent': 'Overtime request sent',
  's2.myAv.hint': '{count} days registered in this period',
  's2.myAv.none': 'Not registered',
  's2.my.state.upcoming': 'Upcoming',
  's2.my.state.ready': 'Today',
  's2.my.state.working': 'Working',
  's2.my.state.paused': 'On a break',
  's2.my.state.done': 'Waiting for approval',
  's2.my.state.approved': 'Approved',
  's2.my.state.approvedPlanned': 'Approved as planned',
  's2.my.state.missed': 'Not registered',
  's2.my.action.checkIn': 'Check in',
  's2.my.action.pause': 'Take a break',
  's2.my.action.resume': 'Back to work',
  's2.my.action.checkOut': 'Check out',
  's2.my.done.checkIn': 'You are checked in',
  's2.my.done.pause': 'Break started',
  's2.my.done.resume': 'Back to work',
  's2.my.done.checkOut': 'Checked out. Your employer approves the hours next.',
  's2.my.summary.planned': 'Planned',
  's2.my.summary.worked': 'Worked',
  's2.my.summary.approved': 'Approved for pay',
  's2.my.summary.awaiting': 'Waiting for approval',
  's2.my.col.planned': 'Planned',
  's2.my.col.clock': 'Checked in',
  's2.my.col.worked': 'Worked',
  's2.my.now': 'now',
  's2.my.untitled': 'Shift',
  's2.my.empty': 'No shifts in this period',
  's2.my.emptyHint': 'Shifts you are given show up here.',
  's2.my.clockOff': 'Checking in is switched off',
  's2.my.clockOffHint': 'Your employer records your hours. You can still follow them here.',
  's2.my.gpsHint': 'Your location is recorded when you check in, take a break and check out.',
  's2.my.locationNeeded': 'We need your location',
  's2.my.locationNeededHint': 'Allow location for this site in your browser, then try again.',
  's2.week': 'Week',
  's2.month': 'Month',
  's2.quarter': 'Quarter',
  's2.days': 'Days',
  's2.individual': 'Individual',
  's2.group': 'Group',
  's2.allEmployees': 'All employees',
  's2.allTypes': 'All types',
  's2.allStatuses': 'All statuses',
  's2.allSources': 'All sources',
  's2.status.confirmed': 'Confirmed',
  's2.status.draft': 'Draft',
  's2.status.open': 'Open',
  's2.source.manual': 'Manual',
  's2.source.ai': 'AI',
  's2.source.group': 'Group',
  's2.source.unscheduled': 'Unscheduled',
  's2.source.unscheduledHint': 'Recorded afterwards: work done without a planned shift',
  's2.legend.available': 'Available',
  's2.legend.preferred': 'Preferred',
  's2.legend.unavailable': 'Unavailable',
  's2.legend.vacation': 'Vacation',
  's2.legend.sickness': 'Sickness',
  's2.legend.absence': 'Absence',
  's2.previousPeriod': 'Previous period',
  's2.nextPeriod': 'Next period',
  's2.sections': 'Scheduler sections',
  's2.table.employee': 'Employee',
  's2.empty.noEmployees': 'No employees found.',
  's2.loadError': 'We could not load availability.',
  's2.register.action': 'Register availability',
  's2.register.title': 'Register availability',
  's2.register.employee': 'Employee',
  's2.register.period': 'Period',
  's2.register.start': 'Start date',
  's2.register.end': 'End date',
  's2.register.type': 'Type',
  's2.register.note': 'Note (optional)',
  's2.register.notePlaceholder': 'e.g. flu',
  's2.register.submit': 'Register',
  's2.register.success': 'Availability registered successfully',
  's2.delete.title': 'Delete availability',
  's2.delete.message': '{name} will have nothing recorded for {date}.',
  's2.delete.success': 'Absence deleted successfully',
  's2.shifts.loadError': 'We could not load the schedule.',
  's2.shifts.newShiftTitle': 'New shift',
  's2.shifts.openShiftTitle': 'Open shift',
  's2.shifts.showShifts': 'Show {count} shifts',
  's2.shifts.minBreak': 'min break',
  's2.shifts.openRow': 'Open shifts',
  's2.shifts.absent': 'Absent',
  's2.shifts.preferred': 'Preferred',
  's2.shifts.moreCount': '{count} more',
  's2.shifts.openWeek': 'Open week',
  's2.shifts.weekColumn': 'Week',
  's2.shifts.add': 'Add shift',
  's2.shifts.moved': 'Shift moved',
  's2.select': 'Select',
  's2.menu.exportExcel': 'Export Excel',
  's2.menu.deleteDrafts': 'Delete {count} drafts',
  's2.menu.deletedDrafts': '{count} drafts deleted',
  's2.export.filePrefix': 'schedule',
  's2.actionBar.selectedCount': '{count} selected',
  's2.actionBar.deleteShifts': 'Delete {count} shifts',
  's2.actionBar.publishDrafts': 'Publish {count} drafts',
  's2.actionBar.deletedShifts': '{count} shifts deleted',
  's2.group.empty': 'No locations configured',
  's2.group.location': 'Location',
  's2.group.noLocations': 'No locations configured',
  's2.group.publish': 'Publish',
  's2.group.defaultName': 'Group',
  's2.group.add': 'Add group',
  's2.group.addLocation': 'Add Location',
  's2.group.locationField': 'Location',
  's2.group.locationExists': 'Location already exists.',
  's2.group.locationAdded': 'Location added',
  's2.group.allLocations': 'All Locations',
  's2.group.fill.all': 'All',
  's2.group.fill.someOpen': 'With open shifts',
  's2.group.fill.empty': 'Empty',
  's2.group.fill.filled': 'Filled',
  's2.group.preview': 'Preview',
  's2.group.autoFill': 'Auto-fill',
  's2.group.autoFillHint': 'Fill open slots with free people who hold each multi-shift’s position',
  's2.group.autoFilled': 'Filled {count} slot(s)',
  's2.group.autoFillNoCandidate':
    '{count} slot(s) left open: nobody free holds the position that day',
  's2.group.autoFillNoPosition': '{count} slot(s) left open in multi-shifts without a position',
  's2.group.autoFillNothing': 'No open slots to fill',
  's2.editor.positionRequired': 'Choose a position',
  's2.group.addMultiShift': 'Add Multi Shift',
  's2.group.slots': 'Slots',
  's2.group.assignClose': 'Close',
  's2.group.assign.noneWithPosition': 'Nobody holds the position {position}.',
  's2.group.multiShift.addTitle': 'Add Multi Shift',
  's2.group.multiShift.editTitle': 'Edit Multi Shift',
  's2.group.multiShift.name': 'Name',
  's2.group.multiShift.startTime': 'Start Time',
  's2.group.multiShift.endTime': 'End Time',
  's2.group.multiShift.numSlots': 'Number of Slots',
  's2.group.multiShift.positionHint':
    'Every slot is worked in this position; people are matched to it by name.',
  's2.group.editTitle': 'Edit Group',
  's2.group.field.name': 'Name',
  's2.group.field.location': 'Location',
  's2.group.field.date': 'Date',
  's2.group.field.notes': 'Notes',
  's2.group.deleteTitle': 'Delete Group',
  's2.group.deleteMessage':
    'This cannot be undone. Multi-shifts and individual shifts in this group will be deleted.',
  's2.group.deleteMultiTitle': 'Delete Multi Shift',
  's2.group.deleteMultiMessage':
    'This cannot be undone. Individual shifts in this multi-shift will be deleted.',
  's2.group.updated': 'Group updated',
  's2.group.deleted': 'Group deleted',
  's2.group.multiShiftSaved': 'Multi-shift saved',
  's2.group.multiShiftDeleted': 'Multi-shift deleted',
  's2.group.loadError': 'We could not load group planning.',
  's2.group.published': 'Groups published',
  's2.group.publishError': 'Could not publish groups',
  's2.group.publishEmpty': 'There are no draft groups with shifts to publish this week.',
  's2.group.validation.positive': 'Must be greater than 0.',
  's2.group.validation.cannotDecrease': 'Cannot be less than the current number of slots.',
  's2.source.aiHint': 'AI-generated shift',
  's2.source.groupHint': 'Shift is part of a group',
  's2.editor.title': 'Edit shift',
  's2.editor.confirmedWarning': 'Editing this shift will change its status to {status}.',
  's2.editor.unassigned': 'Unassigned (open shift)',
  's2.editor.effectiveHours': 'effective working time',
  's2.editor.field.title': 'Title',
  's2.editor.field.date': 'Date',
  's2.editor.field.break': 'Break (min)',
  's2.editor.field.start': 'Start',
  's2.editor.field.end': 'End',
  's2.editor.field.assigned': 'Assigned',
  's2.editor.field.position': 'Position',
  's2.editor.positionPlaceholder': 'Select a position',
  's2.editor.noPositions': 'No positions found',
  's2.editor.field.notes': 'Notes',
  's2.editor.notesPlaceholder': 'Add notes...',
  's2.editor.save': 'Save',
  's2.editor.deleteTitle': 'Delete shift',
  's2.editor.deleteMessage': 'This shift will be removed from the schedule.',
  's2.editor.saved': 'Shift saved',
  's2.editor.deleted': 'Shift deleted',
  's2.publish': 'Publish',
  's2.publish.visible': 'Publish visible',
  's2.publish.periodWeek': 'Publish for selected week',
  's2.publish.periodMonth': 'Publish for selected month',
  's2.publish.periodQuarter': 'Publish for selected quarter',
  's2.publish.title': 'Publish schedule',
  's2.publish.notice': 'Publishing changes drafts to confirmed shifts.',
  's2.publish.now': 'Publish now',
  's2.publish.publishing': 'Publishing…',
  's2.publish.totalDrafts': 'Total drafts',
  's2.publish.affectedEmployees': 'Affected employees',
  's2.publish.scheduledHours': 'Hours scheduled',
  's2.publish.success': 'Schedule published!',
  's2.publish.successDetail': '{count} shifts sent to {employees} employees.',
  's2.publish.error': 'Could not publish schedule',
  's2.publish.empty': 'There are no drafts to publish in this period.',
  's2.publish.singleDraft': 'Publish selected draft',
  's2.publish.selectedDrafts': 'Publish selected drafts',

  's2.tt.loadError': 'We could not load time tracking.',
  's2.tt.unscheduled.add': 'Add unscheduled work',
  's2.tt.unscheduled.title': 'Unscheduled work',
  's2.tt.unscheduled.hint':
    'Work done without a planned shift. It is recorded as checked in and out, and is approved and paid like any other shift.',
  's2.tt.unscheduled.person': 'Person',
  's2.tt.unscheduled.save': 'Add work',
  's2.tt.checkIn': 'Check in',
  's2.tt.checkOut': 'Check out',
  's2.tt.approve': 'Approve',
  's2.tt.approveAsPlanned': 'Approve as planned',
  's2.tt.editTime': 'Edit time',
  's2.tt.reset': 'Reset',
  's2.tt.mode.all': 'All',
  's2.tt.mode.unregistered': 'Unregistered',
  's2.tt.mode.discrepancy': 'Discrepancy',
  's2.tt.mode.manual': 'Manual',
  's2.tt.mode.approved': 'Approved',
  's2.tt.selectAll': 'Select all',
  's2.tt.selectAllDiscrepancy': 'Select all with discrepancy',
  's2.tt.selectAllManual': 'Select all manual',
  's2.tt.approval.worked': 'Approved as worked',
  's2.tt.approval.planned': 'Approved as planned',
  's2.tt.col.employee': 'Employee',
  's2.tt.col.planned': 'Planned',
  's2.tt.col.worked': 'Worked',
  's2.tt.col.normal': 'Normal',
  's2.tt.col.overtime': 'Overtime',
  's2.tt.col.daysAbsence': 'Days absence',
  's2.tt.col.late': 'Late',
  's2.tt.col.total': 'Total',
  's2.tt.rangeStart': 'Start date',
  's2.tt.rangeEnd': 'End date',
  's2.tt.contacts': 'Contact details',
  's2.tt.unpublished': 'Shift is not published',
  's2.tt.publishShift': 'Publish shift',

  'title.signIn': 'Sign in',
  'title.signUp': 'Create an account',
  'title.forgotPassword': 'Reset Password',
  'title.resetPassword': 'Reset Password',
  'title.confirmEmail': 'Confirm email',

  'sched.everyone': 'Everyone',
  'sched.anyStatus': 'Any status',
  'sched.anySource': 'Any source',
  'sched.doneSelecting': 'Done selecting',
  'shiftStatus.draft': 'Draft',
  'shiftStatus.open': 'Open',
  'shiftStatus.confirmed': 'Confirmed',
  'shiftSource.manual': 'Manual',
  'shiftSource.ai': 'Planned by AI',
  'shiftSource.group': 'From a group',

  'sched.anyKind': 'Any kind',
  'sched.anyMonth': 'Any month',
  'sched.unknownPerson': 'Unknown person',
  'sched.nothingRecorded': 'Nothing recorded',
  'sched.shiftAdded': 'Shift added.',
  'sched.shiftUpdated': 'Shift updated.',
  'sched.noLocation': 'No location',
  'sched.noJobTitle': 'No job title',
  'sched.jobTitleDeleteHint': 'Shifts that already reference it keep the name they were given.',
  'sched.templateDeleteHint': 'Shifts already made from it are unaffected.',
  'sched.endBreak': 'End break',
  'sched.startBreak': 'Start break',
  'sched.byWeekday': 'By weekday',
  'sched.pickAShift': 'Pick a shift',
  'sched.yourShift': 'Your shift',
  'sched.theShift': 'The shift',
  'sched.aColleague': 'A colleague',
  'sched.approveRequest': 'Approve request',
  'sched.declineRequest': 'Decline request',
  'sched.openShiftClaims': 'Open shift claims',
  'sched.openShiftUnassigned': 'Open shift (unassigned)',
  'sched.publishConfirmHint':
    'Publishing confirms them and makes them visible to the people assigned to them.',
  'profile.notLoaded': 'Profile has not loaded yet.',

  'sched.completed': 'Completed',
  'sched.totals': 'Totals',
  'day.sun': 'Sun',
  'day.mon': 'Mon',
  'day.tue': 'Tue',
  'day.wed': 'Wed',
  'day.thu': 'Thu',
  'day.fri': 'Fri',
  'day.sat': 'Sat',

  'sched.swaps': 'Swaps',

  'requestStatus.pending': 'Pending',
  'requestStatus.approved': 'Approved',
  'requestStatus.rejected': 'Declined',
  'sched.everything': 'Everything',
  'sched.actionRecorded': '{action} recorded.',

  'sched.day': 'Day',

  'groupType.published': 'Published',

  'sched.hoursWorkedPerWeekdayRange': 'Hours worked per weekday, {start} to {end}',
  'sched.plannedAgainstWorkedRange': 'Planned against worked hours, {start} to {end}',

  // Message templates
  'nt.title': 'Messages',
  'nt.tabTemplates': 'Templates',
  'nt.tabEvents': 'Events',
  'nt.type.all': 'All channels',
  'nt.type.email': 'Email',
  'nt.type.sms': 'SMS',
  'nt.type.inApp': 'In-app',
  'nt.type.push': 'Push notification',
  'nt.version.draft': 'Draft',
  'nt.version.pendingApproval': 'Pending approval',
  'nt.version.approved': 'Approved',
  'nt.version.rejected': 'Rejected',
  'nt.error': 'Could not load message templates',
  'nt.templates.search': 'Search templates…',
  'nt.templates.create': 'Create template',
  'nt.templates.empty': 'No templates found',
  'nt.templates.colName': 'Name',
  'nt.templates.colIdentifier': 'Identifier',
  'nt.templates.colType': 'Channel',
  'nt.templates.colDescription': 'Description',
  'nt.templates.colUpdated': 'Updated',
  'nt.templates.colActiveVersion': 'Active version',
  'nt.templates.activity': 'Activity log',
  'nt.templates.actionsFor': 'Actions for {name}',
  'nt.create.title': 'New template',
  'nt.create.channel': 'Channel',
  'nt.create.name': 'Name',
  'nt.create.identifier': 'Identifier',
  'nt.create.identifierHelper': 'Lowercase slug, unique per channel in this workspace.',
  'nt.create.description': 'Description',
  'nt.create.subject': 'Subject',
  'nt.create.body': 'Body',
  'nt.detail.back': 'Back to templates',
  'nt.detail.newVersion': 'New version',
  'nt.detail.editMetadata': 'Edit details',
  'nt.versions.colVersion': 'Version',
  'nt.versions.colName': 'Name',
  'nt.versions.colStatus': 'Status',
  'nt.versions.colSubject': 'Subject',
  'nt.versions.colCreated': 'Created',
  'nt.versions.colUpdated': 'Updated',
  'nt.versions.fieldName': 'Version name',
  'nt.versions.newTitle': 'New version',
  'nt.versions.newDescription': 'A new draft version will be created from the current content.',
  'nt.versions.create': 'Create',
  'nt.versions.submit': 'Submit for approval',
  'nt.versions.approve': 'Approve',
  'nt.versions.reject': 'Reject',
  'nt.versions.submitted': 'Submitted for approval',
  'nt.activity.title': 'Activity log',
  'nt.activity.colAction': 'Action',
  'nt.activity.colActor': 'Actor',
  'nt.activity.colVersion': 'Version',
  'nt.activity.colCreated': 'Date',
  'nt.activity.templateCreated': 'Template created',
  'nt.activity.templateUpdated': 'Template updated',
  'nt.activity.versionCreated': 'Version created',
  'nt.activity.versionUpdated': 'Version updated',
  'nt.activity.versionSubmitted': 'Submitted for approval',
  'nt.activity.versionApproved': 'Approved',
  'nt.activity.versionRejected': 'Rejected',
  'nt.activity.templatePreviewed': 'Previewed',
  'nt.activity.versionTested': 'Test send',
  'nt.editor.title': 'Edit template',
  'nt.editor.fields': 'Fields',
  'nt.editor.emojis': 'Emojis',
  'nt.editor.visual': 'Visual editor',
  'nt.editor.source': 'HTML source',
  'nt.editor.split': 'Split view',
  'nt.editor.preview': 'Preview',
  'nt.editor.active': 'Active version',
  'nt.editor.noActiveVersion': 'No active version',
  'nt.editor.actions': 'Version actions',
  'nt.editor.save': 'Save changes',
  'nt.editor.selectVersion': 'Version',
  'nt.editor.readOnly':
    'This version cannot be edited while it is pending approval or already approved. Create a new version to make changes.',
  'nt.dialogs.editMetadata': 'Edit template details',
  'nt.dialogs.approveTitle': 'Approve version',
  'nt.dialogs.approveNote': 'Note (optional)',
  'nt.dialogs.rejectTitle': 'Reject version',
  'nt.dialogs.rejectReason': 'Reason',
  'nt.validation.identifier': 'Use lowercase letters, numbers, and hyphens only',
  'nt.validation.body': 'Body cannot be empty',
  'nt.metadata.saved': 'Template details saved',
  'nt.version.saved': 'Version saved',
  'nt.version.created': 'Version created',
  'nt.version.approvedToast': 'Version approved',
  'nt.version.rejectedToast': 'Version rejected',
  'nt.tabDeliveries': 'Deliveries',
  'nt.editor.fieldsFor': 'Fields for event',
  'nt.editor.fieldsGeneral': 'General fields',
  'nt.editor.usedBy': 'Used by the rules of: {events}',
  'comms.channel.email': 'Email',
  'comms.channel.sms': 'SMS',
  'comms.channel.push': 'Push',
  'comms.channel.inApp': 'In-app',
  'comms.channel.slack': 'Slack',
  'comms.channel.notConfigured': 'Not set up on this platform yet',
  'comms.category.people': 'People and access',
  'comms.category.shifts': 'Shifts and time',
  'comms.category.pay': 'Pay and payroll',
  'comms.category.tasks': 'Tasks',
  'comms.category.learning': 'Learning',
  'comms.category.recruiting': 'Recruiting',
  'comms.audience.person': 'For the person',
  'comms.audience.staff': 'For employers',
  'comms.audience.security': 'Security',
  'comms.mode.default': 'Built-in',
  'comms.mode.custom': 'Custom',
  'comms.mode.off': 'Off',
  'comms.target.message': 'People the event concerns',
  'comms.target.roles': 'Everyone with a role',
  'comms.target.addresses': 'Fixed addresses',
  'comms.target.slack': 'Slack channel',
  'comms.role.employer': 'Employers',
  'comms.role.employee': 'Employees',
  'comms.role.contractor': 'Contractors',
  'comms.op.eq': 'equals',
  'comms.op.ne': 'does not equal',
  'comms.op.gt': 'is greater than',
  'comms.op.gte': 'is at least',
  'comms.op.lt': 'is less than',
  'comms.op.lte': 'is at most',
  'comms.op.in': 'is one of',
  'comms.op.exists': 'is set',
  'comms.status.sent': 'Sent',
  'comms.status.failed': 'Failed',
  'comms.status.skipped': 'Skipped',
  'comms.events.title': 'Events',
  'comms.events.intro':
    "Each event is sent through Celeri's built-in channels and texts unless you switch it to your own rules or turn it off.",
  'comms.events.error': 'Could not load the events',
  'comms.events.empty': 'No events to show',
  'comms.events.unconfigured':
    'Not set up on this platform yet: {channels}. Messages through these channels are skipped.',
  'comms.events.builtInChannels': 'Built-in channels',
  'comms.events.modeFor': 'How {name} is sent',
  'comms.events.modeSaved': 'Event setting saved',
  'comms.events.sendAt': 'Sent every day at',
  'comms.events.sendAtHint': 'Norwegian time',
  'comms.events.sendTimeSaved': 'Send time saved',
  'comms.events.platformHint': 'Celeri sends this message itself. It cannot be changed.',
  'comms.events.securityHint': 'Security messages cannot be turned off.',
  'comms.events.showText': 'Show text',
  'comms.events.showRules': 'Show rules ({count})',
  'comms.events.hideDetails': 'Hide details',
  'comms.events.noActiveRules': 'No active rules',
  'comms.events.customEmptyHint':
    'No rule is active, so nothing is sent for this event. Add a rule or activate one.',
  'comms.events.offHint': 'This event is turned off. Nothing is sent for it.',
  'comms.events.builtInText': 'Built-in text',
  'comms.events.customiseHint':
    'Choose Custom to send it your own way: other channels, your templates, other recipients.',
  'comms.rules.title': 'Rules',
  'comms.rules.add': 'Add rule',
  'comms.rules.inactive': 'Inactive',
  'comms.rules.activate': 'Activate',
  'comms.rules.deactivate': 'Deactivate',
  'comms.rules.activated': 'Rule activated',
  'comms.rules.deactivated': 'Rule deactivated',
  'comms.rules.created': 'Rule added',
  'comms.rules.saved': 'Rule saved',
  'comms.rules.deleted': 'Rule deleted',
  'comms.rules.deleteTitle': 'Delete rule',
  'comms.rules.deleteConfirm': 'Delete the rule {name}? The messages it sends stop at once.',
  'comms.rules.actionsFor': 'Actions for {name}',
  'comms.rules.editNamed': 'Edit {name}',
  'comms.rule.newTitle': 'New rule for {event}',
  'comms.rule.editTitle': 'Edit rule for {event}',
  'comms.rule.name': 'Name',
  'comms.rule.nameHint': 'Optional. Shown in the rule list and the delivery log.',
  'comms.rule.channel': 'Channel',
  'comms.rule.text': 'Text',
  'comms.rule.textHint': 'The built-in text, or one of your templates for this channel.',
  'comms.rule.builtInText': 'Built-in text',
  'comms.rule.templateNoVersion': '{name} (no active version)',
  'comms.rule.templateUnknown': 'A template that is no longer available',
  'comms.rule.templatesFailed':
    'Your templates could not be loaded; the built-in text is still available.',
  'comms.rule.slackText': 'Slack messages use the built-in text.',
  'comms.rule.recipients': 'Recipients',
  'comms.rule.peopleOnly': 'Push and in-app messages reach people in the workspace, not addresses.',
  'comms.rule.roles': 'Roles',
  'comms.rule.emails': 'Email addresses',
  'comms.rule.phones': 'Phone numbers',
  'comms.rule.emailsHint': 'One per line, at most {max}.',
  'comms.rule.phonesHint':
    'One per line in international format, such as +4712345678; at most {max}.',
  'comms.rule.slackChannel': 'Slack channel',
  'comms.rule.slackChannelHint': 'The channel name, such as #general, or its ID.',
  'comms.rule.conditions': 'Conditions',
  'comms.rule.conditionsHint': 'Optional. The rule only sends when every condition holds.',
  'comms.rule.noData': 'This event carries no data to set conditions on.',
  'comms.rule.conditionKey': 'Data field',
  'comms.rule.conditionOp': 'Comparison',
  'comms.rule.conditionValue': 'Value',
  'comms.rule.pickCampaign': 'Pick a campaign',
  'comms.sender.title': 'Sender',
  'comms.sender.hint':
    "How your messages present themselves. Emails still leave from Celeri's address. Leave a field empty to keep Celeri's own.",
  'comms.sender.fromName': 'Email sender name',
  'comms.sender.fromNameHint': 'Shown as who the email is from.',
  'comms.sender.fromNamePlaceholder': 'For example: PiP crew',
  'comms.sender.replyTo': 'Reply-to inbox',
  'comms.sender.replyToHint': 'Replies to an email go here.',
  'comms.sender.replyToPlaceholder': 'crew@example.com',
  'comms.sender.sms': 'SMS sender',
  'comms.sender.smsHint':
    "Up to 11 letters and digits. A text from a name cannot be answered, so say in it how to reach you. Where a name is not allowed, Celeri's number is used.",
  'comms.sender.smsPlaceholder': 'PiP',
  'comms.sender.tooLong': 'At most {max} characters.',
  'comms.sender.badEmail': 'Enter an email address.',
  'comms.sender.badSms': 'Up to 11 letters, digits and spaces, with at least one letter.',
  'comms.sender.replies': 'Let people reply by text',
  'comms.sender.repliesHint':
    'Texts go from a number that can be answered, and replies are emailed to the reply-to inbox. Without an inbox, replies are only kept in the delivery log.',
  'comms.sender.saved': 'Sender saved.',
  'comms.rule.conditionListPlaceholder': 'Values, separated by commas',
  'comms.rule.addCondition': 'Add condition',
  'comms.rule.removeCondition': 'Remove condition',
  'comms.rule.active': 'Active',
  'comms.rule.error.nameLength': 'Use at most 120 characters.',
  'comms.rule.error.roles': 'Choose at least one role.',
  'comms.rule.error.addressCount': 'Give between 1 and 20 addresses.',
  'comms.rule.error.email': 'One of the addresses is not a valid email address.',
  'comms.rule.error.phone': 'Write phone numbers with a country code, such as +4712345678.',
  'comms.rule.error.slackChannel': 'Name the Slack channel.',
  'comms.rule.error.slackOnly': 'A Slack rule posts to a Slack channel.',
  'comms.rule.error.noAddresses': 'Push and in-app messages cannot go to addresses.',
  'comms.rule.error.conditionKey': 'Choose a data field.',
  'comms.rule.error.conditionValue': 'Give a value.',
  'comms.rule.error.conditionNumber': 'This comparison needs a number.',
  'comms.deliveries.title': 'Deliveries',
  'comms.deliveries.error': 'Could not load the deliveries',
  'comms.deliveries.searchRecipient': 'Search recipient…',
  'comms.deliveries.allEvents': 'All events',
  'comms.deliveries.allChannels': 'All channels',
  'comms.deliveries.allStatuses': 'All statuses',
  'comms.deliveries.from': 'From date',
  'comms.deliveries.to': 'To date',
  'comms.deliveries.clearFilters': 'Clear filters',
  'comms.deliveries.colTime': 'Time',
  'comms.deliveries.colEvent': 'Event',
  'comms.deliveries.colChannel': 'Channel',
  'comms.deliveries.reply': 'Reply',
  'comms.deliveries.replyLong': 'Reply by text',
  'comms.deliveries.colRecipient': 'Recipient',
  'comms.deliveries.colStatus': 'Status',
  'comms.deliveries.details': 'Details',
  'comms.deliveries.detailsFor': 'Details of the {event} delivery',
  'comms.deliveries.detailTitle': 'Delivery',
  'comms.deliveries.retry': 'Retry',
  'comms.deliveries.retried': 'Tried again; see the new status',
  'comms.deliveries.retrySent': 'Sent again',
  'comms.deliveries.reason': 'Reason',
  'comms.deliveries.rule': 'Rule',
  'comms.deliveries.builtInRule': 'Built-in',
  'comms.deliveries.language': 'Language',
  'comms.deliveries.occurredAt': 'Event time',
  'comms.deliveries.sentAt': 'Sent',
  'comms.deliveries.attempts': 'Attempts',
  'comms.deliveries.providerRef': 'Provider reference',
  'comms.deliveries.source': 'Source',
  'comms.deliveries.message': 'Message',
  'comms.deliveries.subject': 'Subject',
  'comms.deliveries.body': 'Body',
  'profile.notifications.title': 'Notifications',
  'profile.notifications.intro': 'Choose how you hear from this workspace.',
  'profile.notifications.introNamed': 'Choose how you hear from {workspace}.',
  'profile.notifications.error': 'We could not load your notification settings',
  'profile.notifications.email': 'Email',
  'profile.notifications.emailHint': 'Messages to your email address',
  'profile.notifications.sms': 'SMS',
  'profile.notifications.smsHint': 'Text messages to your phone number',
  'profile.notifications.push': 'Push notifications',
  'profile.notifications.pushHint': 'Alerts on your phone from the Celeri app',
  'profile.notifications.inApp': 'In-app',
  'profile.notifications.inAppHint': 'Messages in the notifications panel',
  'profile.notifications.saved': 'Notification settings saved',
  'profile.notifications.securityNote':
    'Security messages, such as a password reset, always arrive, whatever you choose here.',

  // In-app notification inbox (drawer)
  'notifications.empty': "You don't have any notifications yet.",
  'notifications.loadError': 'We could not load your notifications',
  'notifications.status.new': 'New',
  'notifications.status.read': 'Read',
  'notifications.action.accept': 'Accept',
  'notifications.action.decline': 'Decline',
  'notifications.action.reject': 'Reject',
  'notifications.action.fill': 'Fill',
  'notifications.action.ok': 'OK',
  'notifications.bailiffTermination.title': 'Garnishment termination',
  'notifications.bailiffTermination.message':
    "{user_affected}'s tax debt termination date is {date}.",
  'notifications.companyInvitation.title': 'Invitation to {workspace_name}',
  'notifications.companyInvitation.message':
    '{invited_by} has invited you to join {workspace_name}.',
  'notifications.removedFromCompany.title': 'Removed from {workspace_name}',
  'notifications.removedFromCompany.message': '{action_by} removed you from {workspace_name}.',
  'notifications.newInvoice.title': 'New invoice received',
  'notifications.newInvoice.message': 'You received new invoice.',
  'notifications.fixedCycleTerminates.title': 'Fixed payment cycles terminate',
  'notifications.fixedCycleTerminates.message':
    'Some of fixed payment cycles are about to terminate soon.',
  'notifications.employeeSentDraftCycle.title': '{action_by} sent a draft salary cycle',
  'notifications.employeeSentDraftCycle.message':
    'A draft salary cycle from {action_by} awaits your approval.',
  'notifications.employerApprovedDraftCycle.title': 'Your employer approved draft salary cycle',
  'notifications.employerApprovedDraftCycle.message':
    'Your draft salary cycle was approved by {workspace_name}.',
  'notifications.employerRejectedDraftCycle.title': 'Your employer rejected draft salary cycle',
  'notifications.employerRejectedDraftCycle.message':
    'Your draft salary cycle was rejected by {workspace_name}.',
  'notifications.employerCreatedCycle.title': 'Your employer created a salary cycle',
  'notifications.employerCreatedCycle.message': '{workspace_name} created your salary cycle.',
  'notifications.employerSharedCycleFramework.title':
    'Your employer shared a salary cycle framework',
  'notifications.employerSharedCycleFramework.message':
    '{workspace_name} shared new salary cycle framework with you.',
  'notifications.employeeRequestedCycleCancel.title':
    '{action_by} requested for salary cycle cancel',
  'notifications.employeeRequestedCycleCancel.message':
    'A salary cycle cancel was requested by {action_by}. You can either cancel or dismiss this request in the cycle list.',
  'notifications.employerCanceledCycle.title': 'Your employer canceled a salary cycle',
  'notifications.employerCanceledCycle.message':
    '{workspace_name} recently canceled your salary cycle.',
  'notifications.employerDismissedCancelRequest.title': 'Your employer dismissed a request',
  'notifications.employerDismissedCancelRequest.message':
    'Your request for salary cycle cancel was dismissed by {workspace_name}.',
  'notifications.updateNationalId.title': 'Update your National ID',
  'notifications.updateNationalId.message': 'Please update your National ID in profile settings.',

  // Tasker
  'nav.tasker': 'Tasker',
  'nav.taskerTasks': 'Tasks',
  'nav.taskerIncidents': 'Incidents',
  'nav.taskerTemplates': 'Templates',
  'nav.taskerMyTasks': 'My Tasks',
  'nav.taskerRecurrent': 'Recurrent',
  'tk.title': 'Tasker',
  'tk.tabTasks': 'Tasks',
  'tk.tabIncidents': 'Incidents',
  'tk.tabTemplates': 'Templates',
  'tk.tabRecurrent': 'Recurrent',
  'tk.error': 'Could not load tasker data.',
  'tk.type.all': 'All types',
  'tk.type.task': 'Task',
  'tk.type.incident': 'Incident',
  'tk.versionStatus.draft': 'Draft',
  'tk.versionStatus.pending_approval': 'Pending approval',
  'tk.versionStatus.active': 'Active',
  'tk.versionStatus.rejected': 'Rejected',
  'tk.versionStatus.superseded': 'Superseded',
  'tk.recurrenceStatus.active': 'Active',
  'tk.recurrenceStatus.paused': 'Paused',
  'tk.recurrent.new': 'New recurrence',
  'tk.recurrent.create': 'Create recurrence',
  'tk.task.pointsHint': 'Points awarded for completing this task. Use 0 for no points.',
  'ln.editor.checkMet': 'Requirement met',
  'ln.editor.checkUnmet': 'Requirement not met',
  'tk.recurrent.title': 'Recurrencies',
  'tk.recurrent.search': 'Search recurrent tasks…',
  'tk.recurrent.created': 'Recurrent task created',
  'tk.recurrent.pause': 'Pause',
  'tk.recurrent.resume': 'Resume',
  'tk.recurrent.paused': 'Recurrent task paused',
  'tk.recurrent.resumed': 'Recurrent task resumed',
  'tk.recurrent.deleted': 'Recurrent task deleted',
  'tk.recurrent.deleteTitle': 'Delete recurrent task',
  'tk.recurrent.deleteMessage':
    'Are you sure you want to delete {name}? This action cannot be undone.',
  'tk.recurrent.repeat': 'Repeat',
  'tk.recurrent.weekdays': 'Repeat on weekdays',
  'tk.recurrent.lastDay': 'Last day',
  'tk.recurrent.monthDayHint': 'Days 1–28 or last day',
  'tk.recurrent.dates': 'Dates',
  'tk.recurrent.summaryDaily': 'Repeats every day.',
  'tk.recurrent.summaryWeekly': 'Repeats every {days}.',
  'tk.recurrent.summaryMonthly': 'Repeats on day {day} of each month.',
  'tk.recurrent.summaryLastDay': 'Repeats on the last day of each month.',
  'tk.recurrent.summaryOneDate': 'Repeats on 1 selected date.',
  'tk.recurrent.summaryDates': 'Repeats on {count} selected dates.',
  'tk.recurrent.when': 'When should it repeat?',
  'tk.recurrent.frequency.daily': 'Daily',
  'tk.recurrent.frequency.weekly': 'Weekly',
  'tk.recurrent.frequency.monthly': 'Monthly',
  'tk.recurrent.frequency.custom': 'Custom',
  'tk.recurrent.customFrequency': 'Custom Frequency',
  'tk.recurrent.dayOfMonth': 'Day of month',
  'tk.recurrent.eom': 'EOM',
  'tk.recurrent.addDate': 'Add date',
  'tk.recurrent.removeDate': 'Remove {date}',
  'tk.recurrent.daysRequired': 'Choose at least one day',
  'tk.recurrent.datesRequired': 'Add at least one date',
  'tk.recurrent.day.mon': 'Mon',
  'tk.recurrent.day.tue': 'Tue',
  'tk.recurrent.day.wed': 'Wed',
  'tk.recurrent.day.thu': 'Thu',
  'tk.recurrent.day.fri': 'Fri',
  'tk.recurrent.day.sat': 'Sat',
  'tk.recurrent.day.sun': 'Sun',
  'tk.recurrent.everyDay': 'Every day',
  'tk.recurrent.everyWeek': 'Every week',
  'tk.recurrent.monthlyDay': 'Every month on day {day}',
  'tk.recurrent.monthlyLast': 'Every month on the last day',
  'tk.recurrent.onDates': 'On {dates}',
  'tk.recurrent.dueAt': 'due {time}',
  'tk.recurrent.points': '+{points}p',
  'tk.recurrent.upcoming': 'Upcoming occurrences',
  'tk.status.active': 'Active',
  'tk.status.in_progress': 'In progress',
  'tk.status.pending_approval': 'Pending',
  'tk.status.done': 'Done',
  'tk.priority.low': 'Low',
  'tk.priority.medium': 'Medium',
  'tk.priority.high': 'High',
  'tk.tasks.create': 'New task',
  'tk.tasks.search': 'Search tasks…',
  'tk.tasks.allStates': 'All states',
  'tk.tasks.filterAll': 'All',
  'tk.tasks.allAssignees': 'All assignees',
  'tk.tasks.period': 'Period',
  'tk.tasks.colTitle': 'Title',
  'tk.tasks.colType': 'Type',
  'tk.tasks.colState': 'State',
  'tk.tasks.colPriority': 'Priority',
  'tk.tasks.colImpact': 'Impact',
  'tk.tasks.colAssignee': 'Assignee',
  'tk.tasks.colCreated': 'Created',
  'tk.tasks.open': 'Open task',
  'tk.tasks.dueAt': 'Due {when}',
  'tk.tasks.pointsAward': '+{points} points',
  'tk.tasks.followUp': 'follow up',
  'tk.tasks.recurrent': 'Recurrent',
  'tk.tasks.approve': 'Approve',
  'tk.tasks.approveTitle': 'Approve Task',
  'tk.tasks.reject': 'Reject',
  'tk.tasks.rejectTitle': 'Reject task',
  'tk.tasks.rejectMessage': 'Are you sure you want to reject {name}?',
  'tk.tasks.proof': 'Proof',
  'tk.tasks.proofEmpty': 'No proof attached',
  'tk.tasks.proofAttachments': 'Proof Attachments (max 1MB)',
  'tk.tasks.proofHint': 'You need to upload a proof that the task is done, for example, a photo.',
  'tk.tasks.proofRequiredNote': 'This task requires a proof of completion.',
  'tk.incidents.create': 'New incident',
  'tk.incidents.search': 'Search incidents…',
  'tk.incidents.open': 'Open incident',
  'tk.incidents.markDone': 'Mark as done',
  'tk.incidents.reopen': 'Reopen',
  'tk.incidents.approveTitle': 'Approve Incident',
  'tk.incidents.rejectTitle': 'Reject incident',
  'tk.incidents.proofRequiredNote': 'This incident requires a proof of completion.',
  'tk.incidents.followUp': 'Create followup task',
  'tk.incidents.followedUp': 'followed up',
  'tk.incidents.createdAt': 'Created at {when}',
  'tk.incidents.reported': 'Reported {when} by {name}',
  'tk.incidents.responsible': '{name} is responsible',
  'tk.task.unassigned': 'Unassigned',
  'tk.incidents.named': 'Incident: {name}',
  'tk.task.title': 'Task',
  'tk.task.named': 'Task: {name}',
  'tk.task.tabDetails': 'Details',
  'tk.task.tabComments': 'Comments',
  'tk.task.tabAttachments': 'Attachments (max 1MB)',
  'tk.task.tabHistory': 'History',
  'tk.task.fieldTitle': 'Title',
  'tk.task.descriptionPlaceholder': 'Add instructions or context…',
  'tk.task.proofRequiredHint': 'Require evidence when completing this task.',
  'tk.task.createAction': 'Create task',
  'tk.incidents.createAction': 'Create incident',
  'tk.task.fieldDescription': 'Description',
  'tk.task.fieldType': 'Type',
  'tk.task.schedule': 'Task type',
  'tk.task.schedule.normal': 'Normal',
  'tk.task.schedule.recurrent': 'Recurrent',
  'tk.task.fieldTemplate': 'Template',
  'tk.task.fieldState': 'State',
  'tk.task.fieldPriority': 'Priority',
  'tk.task.fieldImpact': 'Impact',
  'tk.task.fieldAssignee': 'Assignee',
  'tk.task.fieldDueDate': 'Due date',
  'tk.task.fieldDueTime': 'Time',
  'tk.task.dueIncomplete': 'Enter both a date and a time',
  'tk.task.fieldAssignedBy': 'Assigned by',
  'tk.task.fieldAssignedAt': 'Assigned at',
  'tk.task.fieldCreatedBy': 'Created by',
  'tk.task.fieldCreated': 'Created',
  'tk.task.noTemplate': 'None (free-form)',
  'tk.task.defaultOption': 'Use default',
  'tk.task.me': 'me',
  'tk.task.closed': 'This task is closed. It can no longer be changed or commented on.',
  'tk.task.reassignComment': 'Handover comment',
  'tk.task.reassignHint':
    'Say why the task changes hands. Saving is possible once this is filled in.',
  'tk.task.handoverPlaceholder': 'Why is the task changing hands?',
  'tk.task.saved': 'Task updated',
  'tk.task.saveAsTemplate': 'Save as template',
  'tk.incidents.created': 'Incident created',
  'tk.task.created': 'Task created',
  'tk.comments.empty': 'No comments yet',
  'tk.comments.add': 'Add comment',
  'tk.comments.placeholder': 'Write a comment…',
  'tk.comments.onlyAssignee': 'Only the current assignee can comment on this task.',
  'tk.comments.added': 'Comment added',
  'tk.comments.delete': 'Delete comment',
  'tk.comments.deleteTitle': 'Delete comment',
  'tk.comments.deleteMessage':
    'Are you sure you want to delete this comment? This action cannot be undone.',
  'tk.comments.deleted': 'Comment deleted',
  'tk.comments.attachments': 'Attachments',
  'tk.comments.attach': 'Attach files (max 1MB)',
  'tk.comments.unstage': 'Remove {name}',
  'tk.attachments.empty': 'No attachments',
  'tk.attachments.upload': 'Upload file',
  'tk.attachments.uploaded': 'File uploaded',
  'tk.attachments.removed': 'File removed',
  'tk.attachments.remove': 'Remove {name}',
  'tk.attachments.download': 'Download {name}',
  'tk.attachments.tooLarge': 'The file is larger than 16 MB.',
  'tk.attachments.rejected':
    'The server refused the file. Uploads larger than 1 MB are rejected by the service at the moment.',
  'tk.preview.title': 'Attachment',
  'tk.preview.open': 'Preview {name}',
  'tk.preview.unavailable': 'No inline preview for this file type.',
  'tk.preview.tooLarge': 'This text file is too large to show inline.',
  'tk.task.assigneeHint': 'Leave empty to assign the task to yourself.',
  'tk.task.templateNoActive':
    'This template has no approved version yet, so no task can be created from it.',
  'tk.history.empty': 'No activity yet',
  'tk.history.unassigned': 'Unassigned',
  'tk.history.by': 'by {name}',
  'tk.history.handedTo': 'handed over to {name}',
  'tk.history.fromTo': '{from} → {to}',
  'tk.timeline.title': 'Activity',
  'tk.timeline.created': 'Task created',
  'tk.timeline.assignedTo': 'Assigned to {name}',
  'tk.timeline.status': 'Status changed',
  'tk.timeline.updated': 'Task updated',
  'tk.timeline.done': 'Marked as done',
  'tk.timeline.submitted': 'Submitted',
  'tk.templates.create': 'New template',
  'tk.templates.empty': 'No templates yet',
  'tk.templates.showArchived': 'Show archived',
  'tk.templates.colName': 'Name',
  'tk.templates.colStatus': 'Status',
  'tk.templates.colCreated': 'Created',
  'tk.templates.archived': 'Archived',
  'tk.templates.active': 'Active',
  'tk.templates.noActiveVersion': 'No active version',
  'tk.templates.archive': 'Archive template',
  'tk.templates.archiveConfirm':
    'Archive “{name}”? It will be hidden from the active library and unavailable for new tasks and incidents. Existing tasks are kept. You can still find it with Show archived; it cannot be restored here.',
  'tk.templates.archivedToast': 'Template archived',
  'tk.templates.createdToast': 'Template created',
  'tk.templates.actionsFor': 'Actions for {name}',
  'tk.templates.open': 'Open template',
  'tk.templates.use': 'Use',
  'tk.templates.useAs': 'Use {name} as',
  'tk.template.name': 'Name',
  'tk.template.description': 'Description',
  'tk.template.descriptionHint':
    'Copied onto every task created from this template that has no description of its own.',
  'tk.priority.named': '{name} priority',
  'tk.template.priority': 'Priority',
  'tk.template.points': 'Points',
  'tk.template.pointsHint': 'Copied onto every task created from this template. Defaults to 0.',
  'tk.template.pointsMin': 'Must be 0 or greater.',
  'tk.template.proofRequired': 'Proof required',
  'tk.template.proofRequiredHint':
    'The worker must attach evidence when completing a task from this template.',
  'tk.template.proofNotRequired': 'Proof not required',
  'tk.template.noDefault': 'No default',
  'tk.template.versions': 'Versions',
  'tk.template.noVersions': 'No versions yet',
  'tk.template.newDraft': 'New draft',
  'tk.template.newDraftFrom': 'New draft from version {version}',
  'tk.template.draftCreated': 'Draft created',
  'tk.versions.colVersion': 'Version',
  'tk.versions.colStatus': 'Status',
  'tk.versions.colAuthor': 'Author',
  'tk.versions.colUpdated': 'Updated',
  'tk.versions.colSubmitted': 'Submitted',
  'tk.versions.edit': 'Edit draft',
  'tk.versions.editTitle': 'Edit draft',
  'tk.versions.saveDraft': 'Save draft',
  'tk.versions.submit': 'Submit for approval',
  'tk.versions.approve': 'Approve',
  'tk.versions.reject': 'Reject',
  'tk.versions.review': 'Review',
  'tk.versions.reviewTitle': 'Review version',
  'tk.versions.submitted': 'Submitted for approval',
  'tk.versions.approved': 'Version approved',
  'tk.versions.rejected': 'Version rejected',
  'tk.versions.saved': 'Draft saved',
  'tk.versions.fourEyes': 'You wrote this version, so someone else has to approve it.',
  'tk.versions.rejectTitle': 'Reject version',
  'tk.versions.rejectReason': 'Reason',
  'tk.versions.approveConfirm':
    'Approve version {version}? It becomes the active version and replaces the current one.',
  'error.tasker.noWorkspace': 'No active workspace selected.',
  'error.tasker.forbidden': 'Your role does not permit this action.',
  'error.tasker.notFound': 'The record was not found.',
  'error.tasker.selfApproval': 'A version cannot be approved by its author.',
  'error.tasker.invalidTransition': 'The record is not in a state that allows this.',
  'error.tasker.activeVersionExists': 'The template already has an active version.',
  'error.tasker.noActiveVersion': 'The template has no active version.',
  'error.tasker.taskClosed': 'The task is closed.',
  'error.tasker.commentRequired': 'A comment is required to reassign a task.',
  'error.tasker.notAssignee': 'Only the current assignee may do this.',
  'error.tasker.elementInUse': 'The element is still referenced by existing tasks.',
  'error.tasker.concurrentUpdate': 'The record changed in the meantime. Reload and try again.',
  'error.tasker.validation': 'The request is not valid.',
  'error.tasker.unavailable': 'The tasker service could not process the request.',
  'error.eml.noWorkspace': 'No active workspace selected.',
  'error.eml.creating': 'This wallet is already being created. Try again shortly.',
  'error.eml.notFound': 'There is no wallet yet.',
  'error.eml.unavailable': 'The payments provider could not be reached.',
  'error.eml.notConfigured': 'Moving money is not set up yet for this workspace.',
  'error.eml.invalidAmount': 'Enter an amount above zero with at most two decimals.',
  'error.eml.invalidRequest': 'The request was incomplete. Try again.',
  'error.eml.paymentNotFound': 'That payment no longer exists.',
  'error.eml.notPayable': 'That payment is not approved, or has already been paid.',
  'error.eml.payeeHasNoWallet': 'The employee has not created a wallet yet.',
  'error.eml.currencyMismatch': "The payment's currency does not match the wallets.",
  'error.eml.walletInactive':
    'The company wallet is not active yet. Top it up first — its first top-up activates it.',
  'error.company.provinceInvalid':
    'The state/province must be an ISO 3166-2 region code, e.g. 03 or BY.',
  'error.company.codeInvalid':
    'The company code (organisation number) is required and must be at most 32 characters.',
  'error.company.emailInvalid': 'Enter a valid email address for the company.',
  'error.company.countryInvalid': 'Choose a country from the list.',
  'error.eml.addressIncomplete':
    "Add the company's country and state/province in Company settings before moving money.",
  'error.eml.cardholderIncomplete':
    "The wallet can't be registered: the cardholder's profile needs a first name, a last name and an email.",
  'error.eml.registrationRefused':
    'The payments provider refused to register the wallet. Nothing was moved.',
  'error.eml.insufficientFunds': 'The company wallet does not hold enough to cover this payment.',
  'error.eml.movementInProgress': 'This is already being processed. Refresh in a moment.',
  'error.eml.needsReview':
    'The provider did not confirm the outcome. It must be checked before continuing.',
  'error.eml.creditPending':
    'The company wallet was debited, but the employee could not be credited yet. Try again.',
  'error.eml.refused': 'The payments provider refused this. Nothing was moved.',

  // --- learner ---
  'nav.learning': 'Learning',
  'nav.learningMy': 'My training',
  'nav.learningCourses': 'Courses',
  'nav.learningPrograms': 'Programmes',
  'nav.learningReviews': 'Reviews',
  'error.learner.noWorkspace': 'No active workspace selected.',
  'error.learner.forbidden': 'Your role does not allow this action.',
  'error.learner.notFound': 'This item no longer exists.',
  'error.learner.validation': 'Some details are missing or invalid.',
  'error.learner.duplicateTitle': 'A course or programme with this name already exists.',
  'error.learner.invalidState': 'That is not possible in its current state.',
  'error.learner.selfReview': 'You cannot approve your own training.',
  'error.learner.concurrentUpdate':
    'Someone else changed this at the same time. Reload and try again.',
  'error.learner.unavailable': 'Training is unavailable right now. Try again shortly.',
  'ln.loadFailed': 'Could not load training.',
  'ln.card.required': 'Required',
  'ln.card.minutes': '{n} min',
  'ln.card.modules': '{n} modules',
  'ln.card.progress': 'Progress',
  'ln.state.notStarted': 'Not started',
  'ln.state.inProgress': 'In progress',
  'ln.state.awaitingPractical': 'Awaiting practical',
  'ln.state.completed': 'Completed',
  'ln.state.renew': 'Renew',
  'ln.certificate.valid': 'Valid',
  'ln.certificate.expiring': 'Expiring soon',
  'ln.certificate.expired': 'Expired',
  'ln.certificate.issued': 'Issued {date}',
  'ln.certificate.expires': 'Expires {date}',
  'ln.certificate.neverExpires': 'Never expires',
  'ln.courseStatus.draft': 'Draft',
  'ln.courseStatus.published': 'Published',
  'ln.courseStatus.archived': 'Archived',
  'ln.external.pending': 'Pending',
  'ln.external.approved': 'Approved',
  'ln.external.rejected': 'Rejected',
  'ln.external.validUntil': 'valid until {date}',
  'ln.external.rejectedBecause': 'Rejected: {reason}',
  'ln.reason.ok': 'Up to date',
  'ln.reason.expiring': 'Expiring',
  'ln.reason.missing': 'Missing',
  'ln.reason.expired': 'Expired',
  'ln.reason.awaitingPractical': 'Awaiting practical',
  'ln.reason.due': 'Due soon',
  'ln.reason.overdue': 'Overdue',
  'ln.readiness.label': 'Readiness for shift',
  'ln.readiness.ready': 'Ready for shift',
  'ln.readiness.readyText': 'All required training is complete and valid.',
  'ln.readiness.notReady': 'Not ready for shift — {n} to finish',
  'ln.readiness.notReadyText': 'These courses must be completed before the next shift:',
  'ln.readiness.dueBy': 'Due by {date}',
  'ln.kind.forklift': 'Forklift licence',
  'ln.kind.siteId': 'Construction site ID card',
  'ln.kind.alcoholService': 'Alcohol service licence',
  'ln.kind.foodHygiene': 'Food hygiene certificate',
  'ln.kind.security': 'Security guard certificate',
  'ln.kind.drivingLicence': 'Driving licence',
  'ln.kind.tradeCertificate': 'Trade certificate',
  'ln.kind.language': 'Language certificate',
  'ln.kind.other': 'Other',
  'ln.lang.no': 'Norwegian',
  'ln.lang.en': 'English',
  'ln.lang.pl': 'Polish',
  'ln.lang.lt': 'Lithuanian',
  'ln.lang.sv': 'Swedish',
  'ln.lang.de': 'German',
  'ln.role.employee': 'Employees',
  'ln.role.contractor': 'Contractors',
  'ln.role.employer': 'Employers',
  'ln.myTraining.title': 'My training',
  'ln.myTraining.tabRequired': 'Required',
  'ln.myTraining.tabAll': 'All courses',
  'ln.myTraining.tabPrograms': 'Programmes',
  'ln.myTraining.tabCertificates': 'Certificates',
  'ln.myTraining.tabExternal': 'External certificates',
  'ln.myTraining.addExternal': 'Add certificate',
  'ln.myTraining.noRequired': 'No required courses right now.',
  'ln.myTraining.noCourses': 'No courses have been published yet.',
  'ln.myTraining.noPrograms': 'You are not on any programme.',
  'ln.myTraining.noCertificates': 'No certificates yet.',
  'ln.myTraining.noExternal': 'No external certificates submitted.',
  'ln.myTraining.noExternalHint':
    'Already hold a licence or certificate? Submit it for approval so you need not take it again.',
  'ln.myTraining.overdue': 'Overdue',
  'ln.myTraining.blocksShift': 'Blocks shifts',
  'ln.player.title': 'Course',
  'ln.player.back': 'Back to my training',
  'ln.player.practical': 'Practical part',
  'ln.player.validFor': 'Valid for {n} months',
  'ln.player.progress': 'Course progress',
  'ln.player.certificateTitle': 'Your certificate',
  'ln.player.renew': 'Renew',
  'ln.player.awaitingPractical': 'Theory passed. An employer will sign off the practical part.',
  'ln.player.modules': 'Modules',
  'ln.player.markRead': 'Mark as read',
  'ln.player.locked': 'Finish the modules before this one first.',
  'ln.player.quiz': 'Quiz',
  'ln.player.passed': 'Passed',
  'ln.player.failed': 'Not passed',
  'ln.player.score': '{correct} of {total} correct — {threshold} needed to pass.',
  'ln.player.yourAnswer': 'Your answer',
  'ln.player.retry': 'Try again',
  'ln.player.backToCourse': 'Back to course',
  'ln.player.attemptIntro':
    'Attempt {n}: answer all {total} questions. {threshold} correct passes.',
  'ln.player.submit': 'Submit answers',
  'ln.player.quizDone': 'You have passed the quiz for this course.',
  'ln.player.quizReady':
    'All modules read. The quiz draws {total} questions; {threshold} correct passes.',
  'ln.player.startQuiz': 'Start quiz',
  'ln.player.resumeQuiz': 'Resume quiz',
  'ln.player.quizLocked': 'Read every module to unlock the quiz.',
  'ln.courses.title': 'Courses',
  'ln.courses.create': 'New course',
  'ln.courses.filterAll': 'All',
  'ln.courses.empty': 'No courses here.',
  'ln.courses.emptyHint': 'Write a course with modules and a quiz, then publish it for your team.',
  'ln.courses.version': 'Version {n}',
  'ln.courses.questions': '{n} questions',
  'ln.courses.updated': 'Updated {date}',
  'ln.courses.edit': 'Edit',
  'ln.courses.publish': 'Publish',
  'ln.courses.publishConfirm':
    'Publish “{title}”? Learners can take it straight away, and it can no longer be edited — changes need a new version.',
  'ln.courses.copy': 'Copy to new draft',
  'ln.courses.archive': 'Archive',
  'ln.courses.archiveConfirm':
    'Archive “{title}”? Learners can no longer start it; certificates already issued stay valid.',
  'ln.editor.newTitle': 'New course',
  'ln.editor.editTitle': 'Edit course',
  'ln.editor.back': 'Back to courses',
  'ln.editor.saveDraft': 'Save draft',
  'ln.editor.frozen':
    'This course is {status} and can no longer be edited. Copy it to a new draft to make changes.',
  'ln.editor.basics': 'Basics',
  'ln.editor.title': 'Title',
  'ln.editor.description': 'Description',
  'ln.editor.validity': 'Certificate validity',
  'ln.editor.validityHint': 'How long a pass counts before it must be renewed.',
  'ln.editor.never': 'Never expires',
  'ln.editor.months': '{n} months',
  'ln.editor.required': 'Required',
  'ln.editor.requiredHint': 'Everyone must hold it to be ready for shift.',
  'ln.editor.practical': 'Practical part',
  'ln.editor.practicalHint': 'An employer signs off in person after the quiz.',
  'ln.editor.languages': 'Languages',
  'ln.editor.modules': 'Modules',
  'ln.editor.untitledModule': 'Untitled module',
  'ln.editor.moveUp': 'Move up',
  'ln.editor.moveDown': 'Move down',
  'ln.editor.removeModule': 'Remove module',
  'ln.editor.moduleTitle': 'Module title',
  'ln.editor.moduleText': 'Content',
  'ln.editor.moduleTextHint': 'One paragraph per line.',
  'ln.editor.video': 'Video link',
  'ln.editor.videoHint': 'A direct link to an .mp4, .webm, .m4v or .mov file.',
  'ln.editor.videoInvalid': 'This link cannot be played. Use a direct link to a video file.',
  'ln.editor.addModule': 'Add module',
  'ln.editor.quiz': 'Quiz',
  'ln.editor.quizHint':
    'Each attempt draws questions at random from the bank below, so a retry is not the same test.',
  'ln.editor.draw': 'Questions per attempt',
  'ln.editor.drawHint': 'Between 1 and {n}.',
  'ln.editor.threshold': 'Correct to pass',
  'ln.editor.thresholdHint': 'No more than the questions per attempt.',
  'ln.editor.questionShort': 'Q{n}',
  'ln.editor.removeQuestion': 'Remove question',
  'ln.editor.prompt': 'Question',
  'ln.editor.answers': 'Answers',
  'ln.editor.answersHint': 'Mark the correct answer.',
  'ln.editor.markCorrect': 'Mark answer {letter} as correct',
  'ln.editor.answer': 'Answer {letter}',
  'ln.editor.removeAnswer': 'Remove answer',
  'ln.editor.addAnswer': 'Add answer',
  'ln.editor.addQuestion': 'Add question',
  'ln.editor.checklist': 'Ready to publish?',
  'ln.editor.checklistHint': 'A draft saves with only a title. Before publishing:',
  'ln.editor.readyToPublish': 'Everything needed is in place.',
  'ln.problem.title': 'Give the course a title.',
  'ln.problem.titleLong': 'Keep the title under {n} characters.',
  'ln.problem.language': 'Pick at least one language.',
  'ln.problem.modules': 'Add at least one module.',
  'ln.problem.modulesMany': 'A course has at most {n} modules.',
  'ln.problem.moduleTitle': 'Module {n} needs a title.',
  'ln.problem.moduleText': 'Module {n} needs content.',
  'ln.problem.moduleVideo': 'Module {n}: the video link cannot be played.',
  'ln.problem.draw': 'Questions per attempt must be between 1 and {n}.',
  'ln.problem.threshold':
    'The pass mark must be at least 1 and no more than the questions per attempt.',
  'ln.problem.bank': 'Add at least {n} questions to draw from.',
  'ln.problem.bankMany': 'A question bank has at most {n} questions.',
  'ln.problem.questionText': 'Question {n} needs text.',
  'ln.problem.questionOptions': 'Question {n} needs at least two answers.',
  'ln.problem.questionEmptyOption': 'Question {n} has an empty answer.',
  'ln.problem.questionCorrect': 'Question {n} has no correct answer marked.',
  'ln.programs.title': 'Programmes',
  'ln.programs.create': 'New programme',
  'ln.programs.showArchived': 'Show archived',
  'ln.programs.empty': 'No programmes yet.',
  'ln.programs.emptyHint':
    'Bundle courses, assign them to a role or to people, and set a date to finish by.',
  'ln.programs.people': '{n} people',
  'ln.programs.everyone': 'All {role}',
  'ln.programs.edit': 'Edit',
  'ln.programs.archive': 'Archive',
  'ln.programs.archiveConfirm':
    'Archive “{name}”? It will stop assigning training and affecting readiness. Existing learning progress and certificates are kept. You can still find it with Show archived; it cannot be restored here.',
  'ln.programForm.newTitle': 'New programme',
  'ln.programForm.editTitle': 'Edit programme',
  'ln.programForm.name': 'Name',
  'ln.programForm.due': 'Finish by',
  'ln.programForm.dueHint': 'The last day to complete every course.',
  'ln.programForm.blocks': 'Blocks shifts when missed',
  'ln.programForm.blocksHint':
    'After the date, anyone who has not finished is not ready for shift.',
  'ln.programForm.courses': 'Courses',
  'ln.programForm.noCourses': 'Publish a course first.',
  'ln.programForm.coursesRequired': 'Pick at least one course.',
  'ln.programForm.retiredCourse': 'Course no longer published',
  'ln.programForm.assign': 'Assign to',
  'ln.programForm.byRole': 'Everyone in a role',
  'ln.programForm.byPeople': 'Chosen people',
  'ln.programForm.role': 'Role',
  'ln.programForm.people': 'People ({n} chosen)',
  'ln.programForm.searchPeople': 'Search by name or email',
  'ln.programForm.noPeople': 'No one matches.',
  'ln.programForm.peopleRequired': 'Pick at least one person.',
  'ln.programForm.save': 'Save programme',
  'ln.externalForm.title': 'Add a certificate',
  'ln.externalForm.hint':
    'An employer reviews it. Once approved, it counts like training taken here.',
  'ln.externalForm.kind': 'Type',
  'ln.externalForm.kindPlaceholder': 'Choose a type',
  'ln.externalForm.name': 'Name on the certificate',
  'ln.externalForm.nameHint': 'As written on the document, e.g. “Truckførerbevis T1–T4”.',
  'ln.externalForm.country': 'Country',
  'ln.externalForm.countryHint': 'Two-letter code, e.g. NO, SE, PL.',
  'ln.externalForm.countryInvalid': 'Use a two-letter country code.',
  'ln.externalForm.validUntil': 'Valid until',
  'ln.externalForm.validUntilHint': 'Leave empty if it does not expire.',
  'ln.externalForm.dateInvalid': 'Enter a valid date.',
  'ln.externalForm.issuer': 'Issued by',
  'ln.externalForm.level': 'Level',
  'ln.externalForm.levelPlaceholder': 'Choose a CEFR level',
  'ln.externalForm.submit': 'Submit for approval',
  'ln.reviews.title': 'Reviews',
  'ln.reviews.tabPractical': 'Practical sign-off',
  'ln.reviews.tabExternal': 'External certificates',
  'ln.reviews.tabPeople': 'People',
  'ln.reviews.tabVerify': 'Verify certificate',
  'ln.reviews.noPractical': 'Nothing waiting for sign-off.',
  'ln.reviews.noPracticalHint':
    'People who pass the theory of a course with a practical part appear here.',
  'ln.reviews.theoryPassed': 'theory passed {date}',
  'ln.reviews.signOff': 'Sign off',
  'ln.reviews.noExternal': 'No certificates here.',
  'ln.reviews.approve': 'Approve',
  'ln.reviews.reject': 'Reject',
  'ln.reviews.rejectTitle': 'Reject certificate',
  'ln.reviews.rejectHint': '“{title}” — the holder sees the reason, so say what to do differently.',
  'ln.reviews.reason': 'Reason',
  'ln.reviews.person': 'Person',
  'ln.reviews.pickPerson': 'Choose someone',
  'ln.reviews.verifyHint':
    'Enter the code printed on a certificate to check that it is genuine and still valid.',
  'ln.reviews.code': 'Certificate code',
  'ln.reviews.verify': 'Check',
  'ln.reviews.unknownCode': 'No certificate has this code.',
  'ln.reviews.codeValid': 'Genuine and valid',
  'ln.reviews.codeInvalid': 'Genuine, but no longer valid',
  'ln.toast.courseSaved': 'Course saved.',
  'ln.toast.coursePublished': 'Course published.',
  'ln.toast.courseArchived': 'Course archived.',
  'ln.toast.courseCopied': 'Copied to a new draft.',
  'ln.toast.programSaved': 'Programme saved.',
  'ln.toast.programArchived': 'Programme archived.',
  'ln.toast.practicalApproved': 'Practical part signed off.',
  'ln.toast.externalApproved': 'Certificate approved.',
  'ln.toast.externalRejected': 'Certificate rejected.',
  'ln.toast.externalSubmitted': 'Certificate submitted for approval.',
  'ln.toast.renewStarted': 'Renewal started — take the course again.',
  // --- app modules & payments ---
  'nav.payments': 'Payments',
  'error.app.notEnabled':
    'This module is not switched on for your company. An employer can enable it under My apps.',
  'error.app.notPaid':
    'This module is waiting for payment. It opens once the subscription is settled.',
  'payments.companySection': 'Company wallet',
  'payments.mySection': 'My wallet',
  'payments.movements': 'Wallet history',
  'payments.myMovements': 'Payments received',
  'payments.movementsError': 'Could not load the history.',
  'payments.noMovements': 'No money has moved yet.',
  'payments.kind.topUp': 'Top-up',
  'payments.kind.paid': 'Payment sent',
  'payments.kind.received': 'Payment received',
  'payments.status.inProgress': 'In progress',
  'payments.status.completed': 'Completed',
  'payments.status.failed': 'Failed',
  'payments.status.needsReview': 'Needs review',
  'error.payments.forbidden': 'Only an employer can pay from the company wallet.',
  'dashboard.payments.cancelled': 'Payment request cancelled.',
  'dashboard.paymentRequest.open': 'New request',
  'dashboard.paymentRequest.title': 'New payment request',
  'dashboard.paymentRequest.hint':
    'Enter a payment by hand. It is added to the list below and paid from the company wallet when you choose to pay it.',
  'dashboard.paymentRequest.employee': 'Pay to',
  'dashboard.paymentRequest.employeePlaceholder': 'Choose an employee',
  'dashboard.paymentRequest.amount': 'Amount',
  'dashboard.paymentRequest.amountHint': "Net amount, paid in the wallets' currency.",
  'dashboard.paymentRequest.amountInvalid':
    'Enter an amount above 0, up to 1 000 000, with at most two decimals.',
  'dashboard.paymentRequest.date': 'Date',
  'dashboard.paymentRequest.dateInvalid': 'Enter a valid date.',
  'dashboard.paymentRequest.description': 'What it is for',
  'dashboard.paymentRequest.descriptionHint': 'Shown to you in the list and to the employee.',
  'dashboard.paymentRequest.descriptionTooLong': 'At most {n} characters.',
  'dashboard.paymentRequest.submit': 'Create request',
  'dashboard.paymentRequest.created': 'Payment request created. Pay it from the list when ready.',
  'dashboard.paymentRequest.cancel': 'Cancel request',
  'dashboard.paymentRequest.cancelTitle': 'Cancel payment request',
  'dashboard.paymentRequest.cancelMessage':
    'Cancel the request to pay {amount} to {name}? It is removed from the list and cannot be paid.',
  'error.payments.invalidAmount':
    'The amount must be above 0, at most 1 000 000, with at most two decimals.',
  'error.payments.invalidDescription': 'Say what the payment is for, in at most 200 characters.',
  'error.payments.invalidDate': 'The date is not valid.',
  'error.payments.employeeNotFound': 'That employee is not in this company.',
  'error.payments.requestNotFound': 'This payment request no longer exists.',
  'error.payments.requestNotCancellable':
    'This request is being paid or has been paid, so it can no longer be cancelled.',
  'dashboard.wallet.cards': 'Wallets',
  'dashboard.invites.title': '{company} invites you back',
  'dashboard.invites.text': 'Join the crew for {campaign}? Answer by {date}.',
  'dashboard.invites.join': 'Join again: apply',
  'dashboard.invites.keep': 'Keep my profile for later',
  'dashboard.invites.delete': 'Delete my profile there',
  'dashboard.invites.deleteConfirm':
    'This takes you out of {company} now: you lose access and your applications there are deleted. Your Celeri account stays, and so do the shifts you worked.',
  'dashboard.invites.deleteNow': 'Delete it',
  'dashboard.invites.left': 'You are no longer part of {company}.',
  'dashboard.invites.kept': 'Thanks! Your profile is kept for later.',
  'dashboard.lead.title': 'Applicants for your team',
  'dashboard.lead.text': 'Waiting: {total}. Not proposed by you yet: {open}.',
  'dashboard.lead.open': 'View applicants',
  'dashboard.invites.joinedLater':
    'You said you join again. Applications are not open yet; you will hear when they are.',
  'dashboard.wallet.kindCompany': 'Business debit',
  'dashboard.wallet.kindPersonal': 'Debit',
  'dashboard.wallet.holderCompany': 'Company',
  'dashboard.wallet.holderPersonal': 'Card holder',
  'dashboard.paymentRequest.noMatch': 'No one matches',
  'payments.tab.requests': 'Payment requests',
  'payments.tab.history': 'History',
  'payments.history.company': 'Company wallet',
  'payments.history.mine': 'My wallet',
  'dashboard.payments.state.noAmount': 'No amount',
  'dashboard.payments.hint.noAmount': 'This record has no amount to pay.',
  'dashboard.payments.state.interrupted': 'Interrupted',
  'dashboard.payments.hint.interrupted':
    'The provider did not answer before anything was sent. No money has moved; try again to continue this payment.',

  // Payroll by Payout Partner — internal/eor in celeri-be.
  'nav.payroll': 'Payroll',
  'payroll.subtitle':
    'Employ event staff through Payout Partner. They run payroll, tax and the A-melding and invoice your client; you approve the hours.',
  'payroll.notConfigured.heading': 'Payout Partner is not connected yet',
  'payroll.notConfigured.description':
    'The API key has not been set on the server. You can prepare clients and settings, but nothing can be sent until it is.',
  'payroll.tab.pay': 'Pay hours',
  'payroll.nav.section': 'Payout Partner',
  'payroll.config.title': 'Payout Partner',
  'payroll.config.description':
    'Settings for Payout Partner and the clients your work is invoiced to.',
  'payroll.workers.description':
    "Payout Partner's view of your workers: who is enrolled, their payroll details and status. It is separate from the main user list.",
  'payroll.tab.payouts': 'Payouts',
  'payroll.tab.workers': 'Payroll status',
  'payroll.tab.clients': 'Clients',
  'payroll.tab.settings': 'Settings',
  'payroll.nav.settings': 'Payroll settings',
  'payroll.pay.title': 'Approved hours',
  'payroll.pay.error': 'We could not load the hours',
  'payroll.pay.empty.heading': 'No hours to pay',
  'payroll.pay.empty.description':
    'Approved shifts appear here until they are sent to Payout Partner.',
  'payroll.pay.needClient':
    'Add a client first: Payout Partner invoices the work to a client of yours.',
  'payroll.pay.addClient': 'Add one in the app settings',
  'payroll.pay.client': 'Invoice to',
  'payroll.pay.clientPlaceholder': 'Choose a client',
  'payroll.pay.event': 'Event',
  'payroll.pay.noEvent': 'No event',
  'payroll.pay.selectAll': 'Select all ready ({count})',
  'payroll.pay.select': 'Select {name}',
  'payroll.pay.hours': '{hours} h',
  'payroll.pay.rate': '{rate}/h',
  'payroll.pay.code': 'code {code}',
  'payroll.pay.ready': 'Ready',
  'payroll.pay.selected': '{count} selected · {gross} gross',
  'payroll.pay.review': 'Review payouts',
  'payroll.pay.sent': '{count} payout(s) sent to Payout Partner',
  'payroll.pay.dialog.title': 'Send to Payout Partner',
  'payroll.pay.dialog.loading': 'Preparing the payouts…',
  'payroll.pay.dialog.invoicedTo': 'Invoiced to',
  'payroll.pay.dialog.noDraft':
    'Payout Partner has no draft: each payout is submitted as soon as it is sent and cannot be changed here afterwards.',
  'payroll.pay.dialog.leftOut': 'Left out',
  'payroll.pay.dialog.nothing': 'None of the selected hours can be sent.',
  'payroll.pay.dialog.payout': 'Payout',
  'payroll.pay.dialog.total': 'Total {total} gross',
  'payroll.pay.dialog.send': 'Send {count} payout(s)',
  'payroll.line.month': 'Month',
  'payroll.line.code': 'Code',
  'payroll.line.days': 'Days',
  'payroll.line.hours': 'Hours',
  'payroll.line.rate': 'Rate',
  'payroll.blocked.notEnrolled': 'Worker not enrolled',
  'payroll.blocked.notApproved': 'Enrollment not approved',
  'payroll.blocked.noCode': 'No occupation code',
  'payroll.blocked.codeDisabled': 'Code not accepted',
  'payroll.blocked.notNok': 'Not in NOK',
  'payroll.blocked.noHours': 'No hours',
  'payroll.blocked.noRate': 'No hourly rate',
  'payroll.blocked.ratePrecision': 'Rate finer than øre',
  'payroll.blocked.badInterval': 'Worked time is invalid',
  'payroll.blocked.inProgress': 'Being paid',
  'payroll.blocked.notFound': 'Record not found',
  'payroll.blocked.unknown': 'Cannot be paid',
  'payroll.payouts.title': 'Sent to Payout Partner',
  'payroll.payouts.error': 'We could not load the payouts',
  'payroll.payouts.empty.heading': 'Nothing sent yet',
  'payroll.payouts.empty.description': 'Payouts appear here once you send approved hours.',
  'payroll.payouts.sentOn': 'sent {date}',
  'payroll.payouts.invoice': 'invoice {number}',
  'payroll.payouts.paidOn': 'paid out {date}',
  'payroll.payouts.check': 'Check status',
  'payroll.payouts.retry': 'Retry',
  'payroll.payout.state.pending': 'Preparing',
  'payroll.payout.state.submitting': 'Sending',
  'payroll.payout.state.needsReview': 'Unconfirmed',
  'payroll.payout.state.failed': 'Refused',
  'payroll.payout.state.submitted': 'With payroll',
  'payroll.payout.state.approved': 'Approved',
  'payroll.payout.state.published': 'Invoiced',
  'payroll.payout.state.paid': 'Paid out',
  'payroll.payout.state.resolved': 'Settled',
  'payroll.payout.state.rejected': 'Rejected by PoP',
  'payroll.payout.state.nullified': 'Cancelled',
  'payroll.workers.title': 'Payroll status',
  'payroll.workers.error': 'We could not load the workers',
  'payroll.workers.empty.heading': 'No workers',
  'payroll.workers.empty.description': 'Employees of this workspace appear here.',
  'payroll.workers.summary':
    '{approved} payable · {waiting} waiting for Payout Partner · {blocked} to enroll or fix',
  'payroll.workers.enroll': 'Enroll',
  'payroll.workers.resend': 'Update details',
  'payroll.workers.resendHint': 'Send changed name, bank account or address to Payout Partner',
  'payroll.workers.sync': 'Check status',
  'payroll.worker.enrolled': 'Sent to Payout Partner',
  'payroll.worker.state.none': 'Not enrolled',
  'payroll.worker.state.blocked': 'Profile incomplete',
  'payroll.worker.state.refused': 'Refused',
  'payroll.worker.state.pending': 'Pending at PoP',
  'payroll.worker.state.approved': 'Payable',
  'payroll.worker.state.paused': 'Paused',
  'payroll.field.email': 'Email',
  'payroll.field.firstName': 'First name',
  'payroll.field.lastName': 'Last name',
  'payroll.field.birthDate': 'Date of birth',
  'payroll.field.personalNumber': 'National identity number',
  'payroll.field.bankAccount': 'Bank account',
  'payroll.field.street': 'Street',
  'payroll.field.postCode': 'Postcode',
  'payroll.field.city': 'City',
  'payroll.field.profile': 'Profile',
  'payroll.problem.required': 'missing',
  'payroll.problem.personalNumberFormat': 'must be 11 digits',
  'payroll.problem.personalNumberChecksum': 'the check digits do not match (likely a typo)',
  'payroll.problem.personalNumberDate': 'does not contain a real date',
  'payroll.problem.personalNumberKind': 'this kind of number cannot be used for payroll',
  'payroll.problem.birthDateMismatch': 'does not match the date of birth',
  'payroll.clients.title': 'Clients',
  'payroll.clients.error': 'We could not load the clients',
  'payroll.clients.empty.heading': 'No clients yet',
  'payroll.clients.empty.description':
    'Payout Partner invoices the hours to a client of yours. Add the clients you staff events for.',
  'payroll.clients.add': 'Add client',
  'payroll.clients.org': 'Org. no. {org}',
  'payroll.clients.eventCount': '{count} event(s)',
  'payroll.clients.archived': 'Archived',
  'payroll.clients.showArchived': 'Show archived ({count})',
  'payroll.clients.hideArchived': 'Hide archived',
  'payroll.client.newTitle': 'New client',
  'payroll.client.editTitle': 'Edit client',
  'payroll.client.hint':
    'Payout Partner identifies a client by its organisation number, or by email when it has none. A Norwegian number is filled in from Brønnøysundregistrene.',
  'payroll.client.name': 'Name',
  'payroll.client.orgNumber': 'Organisation number',
  'payroll.client.orgNumberHint': 'Nine digits',
  'payroll.client.orgNumberInvalid': 'Not a valid Norwegian organisation number',
  'payroll.client.email': 'Invoice email',
  'payroll.client.emailInvalid': 'Not an email address',
  'payroll.client.identityRequired': 'Enter an organisation number or an email.',
  'payroll.client.required': 'Required',
  'payroll.client.address': 'Address',
  'payroll.client.postalCode': 'Postcode',
  'payroll.client.city': 'City',
  'payroll.client.events': 'Events',
  'payroll.client.eventsHint':
    'Hours are bundled per event on the invoice. The reference is yours, e.g. EVT-2026-01.',
  'payroll.client.eventRef': 'Reference',
  'payroll.client.eventName': 'Event name',
  'payroll.client.eventFrom': 'From',
  'payroll.client.eventTo': 'To',
  'payroll.client.addEvent': 'Add event',
  'payroll.client.removeEvent': 'Remove event',
  'payroll.client.archive': 'Archive',
  'payroll.client.restore': 'Restore',
  'payroll.client.save': 'Save client',
  'payroll.client.saved': 'Client saved',
  'payroll.settings.defaultsTitle': 'Defaults',
  'payroll.settings.defaultCode': 'Default occupation code',
  'payroll.settings.defaultCodeHint': 'Used for hours whose employment names no occupation code.',
  'payroll.settings.codePlaceholder': 'Search codes',
  'payroll.settings.noCodes': 'No codes; sync them below',
  'payroll.settings.defaultClient': 'Default client',
  'payroll.settings.defaultClientHint': 'Preselected when you pay hours.',
  'payroll.settings.noDefaultClient': 'None',
  'payroll.settings.saved': 'Settings saved',
  'payroll.settings.codesTitle': 'Occupation codes',
  'payroll.settings.codesSummary': '{enabled} of {total} codes are accepted by Payout Partner.',
  'payroll.settings.codesHint':
    'Payout Partner decides which codes it accepts. Ask them to enable a code your roles need, then sync.',
  'payroll.settings.syncCodes': 'Sync codes',
  'payroll.settings.codesSynced': 'Occupation codes updated',
  'error.payroll.forbidden': 'Only an employer can manage payroll.',
  'error.payroll.noWorkspace': 'Choose a company first.',
  'error.comms.forbidden': 'Only an employer can change how the workspace communicates.',
  'error.comms.noWorkspace': 'Choose a company first.',
  'error.comms.notFound': 'It no longer exists. Refresh and try again.',
  'error.comms.invalid': 'The change was not accepted.',
  'error.comms.retryUnavailable':
    'This message carried a one-time link and cannot be sent again. Ask for a new one instead.',
  'error.comms.notFailed': 'Only a failed delivery can be retried. It may already have been sent.',
  'error.comms.unavailable':
    'The communications service is not available right now. Try again shortly.',
  'error.comms.badRequest': 'The request could not be read.',
  'error.payroll.notConfigured': 'Payout Partner is not connected yet.',
  'error.payroll.unavailable': 'Payout Partner could not be reached. Try again shortly.',
  'error.payroll.refused': 'Payout Partner refused the request.',
  'error.payroll.invalid': 'Some details are missing or invalid.',
  'error.payroll.notFound': 'It no longer exists.',
  'error.payroll.nothingToPay': 'None of the selected hours can be paid.',
  'error.payroll.clientRequired': 'Choose the client the work is invoiced to.',
  'error.payroll.recordTaken': 'Some of the hours were paid another way meanwhile.',

  // Payroll tab in the user view.
  'myApps.onlyIn': 'Only for companies in {countries}',
  'payroll.profile.tab': 'Payroll',
  'payroll.profile.employments': 'Employments',
  'payroll.profile.employmentsHint':
    'Payout Partner reports each hour under the employment covering that day: its occupation code and hourly rate.',
  'payroll.profile.addEmployment': 'Add employment',
  'payroll.profile.noEmployments': 'No employments yet.',
  'payroll.profile.default': 'Default',
  'payroll.profile.remove': 'Remove employment',
  'payroll.profile.from': 'from {date}',
  'payroll.profile.noPositions': 'No positions yet. Add them under Users → Positions.',
  'payroll.profile.hourlyRate': 'Hourly rate (NOK)',
  'payroll.profile.position': 'Position %',
  'payroll.profile.makeDefault': 'Use when several employments cover the same day',
  'payroll.profile.rateInvalid': 'An amount of 0 or more with at most two decimals',
  'payroll.profile.volunteer': 'Volunteer · unpaid',
  'payroll.profile.rateVolunteerHint': '0 or empty for a volunteer',
  'payroll.blocked.unpaid': 'Volunteer position: unpaid',
  'error.payroll.unpaidPosition':
    'This work is in a volunteer position, which is unpaid: no payment can be made for it.',
  's2.editor.volunteer': 'volunteer',
  'payroll.profile.saved': 'Employments saved',
  'payroll.profile.taxCard': 'Tax card',
  'payroll.profile.syncTaxCard': 'Refresh from Payout Partner',
  'payroll.profile.taxRate': 'Tax rate',
  'payroll.profile.taxCardStatus': 'Tax card',
  'payroll.profile.taxCardValid': 'Valid',
  'payroll.profile.taxCardMissing': 'Missing',
  'payroll.profile.frikort': 'Frikort left',
  'payroll.profile.taxCardSource':
    'From Payout Partner, updated {date}. Payout Partner fetches the tax card as the employer.',
  'payroll.profile.taxCardNotYet':
    'Payout Partner has not reported a tax card yet. Refresh to check.',
  'payroll.profile.taxCardNeedsEnrollment':
    'The tax card appears once the worker is enrolled with Payout Partner (Payroll → Workers).',
  'error.payroll.notEnrolled': 'The worker is not enrolled with Payout Partner yet.',
  'error.app.notInCountry': "This app is not offered in your company's country.",

  // Payroll payout account: wallet IBAN or bank account.
  'payroll.field.payoutAccount': 'Payout account',
  'payroll.account.title': 'Payout account',
  'payroll.account.hint':
    'Where Payout Partner pays the salary. Payout Partner emails the worker each time it changes.',
  'payroll.account.wallet': 'Celeri wallet',
  'payroll.account.bank': 'Bank account',
  'payroll.account.bankHint': 'The account on the worker’s profile',
  'payroll.account.walletAssignedOnSend': 'The IBAN is assigned when it is first sent',
  'payroll.account.paidToWallet': 'Paid to: Celeri wallet {account}',
  'payroll.account.paidToBank': 'Paid to: bank account {account}',
  'payroll.account.useWallet': 'Pay to wallet',
  'payroll.account.useBank': 'Pay to bank account',
  'payroll.account.resend': 'Update at Payout Partner',
  'payroll.account.resendHint':
    'Send the current account, e.g. after a new wallet. Nothing is sent if it has not changed.',
  'payroll.account.saved': 'Payout account saved',
  'payroll.account.problem.noWallet': 'The worker has no Celeri wallet',
  'payroll.account.problem.notActive': 'The wallet needs its first payment before it gets an IBAN',
  'payroll.account.problem.pending': 'The wallet’s IBAN is still being assigned; try again shortly',
  'payroll.account.problem.notSepa': 'Payout Partner only pays to SEPA IBANs',
  'payroll.account.problem.invalid': 'The wallet’s IBAN is not valid',
  'payroll.account.problem.unavailable': 'The wallet cannot be read right now',
  'nav.recruiting': 'Recruiting',
  'rc.join.title': 'Apply',
  'rc.status.draft': 'Draft',
  'rc.status.open': 'Open',
  'rc.status.paused': 'Paused',
  'rc.status.closed': 'Closed',
  'rc.app.title': 'Applicants',
  'rc.app.all': 'All applicants',
  'rc.app.empty': 'No applicants yet.',
  'rc.app.applied': 'Applied',
  'rc.app.waitlisted': 'Waitlisted',
  'rc.app.accepted': 'Accepted',
  'rc.app.declined': 'Declined',
  'rc.app.withdrawn': 'Withdrawn',
  'rc.app.colPerson': 'Applicant',
  'rc.app.colRole': 'Role',
  'rc.app.colChannel': 'Source',
  'rc.app.colStatus': 'Status',
  'rc.app.accept': 'Accept',
  'rc.app.waitlist': 'Waitlist',
  'rc.app.decline': 'Decline',
  'rc.app.decided': '{name}: {status}',
  'rc.crew.employee': 'Paid staff',
  'rc.crew.volunteer': 'Volunteer',
  'rc.crew.contractor': 'Contractor',
  'rc.channel.finn': 'Finn',
  'rc.channel.instagram': 'Instagram',
  'rc.channel.facebook': 'Facebook',
  'rc.channel.website': 'Website',
  'rc.channel.club': 'Club',
  'rc.channel.other': 'Other',
  'rc.channel.returning': 'Returning crew',
  'rc.campaigns.add': 'New campaign',
  'rc.campaigns.addTitle': 'New campaign',
  'rc.campaigns.error': 'We could not load campaigns',
  'rc.campaigns.empty': 'No campaigns yet.',
  'rc.campaigns.emptyHint':
    'Create a campaign, add the positions you recruit for, and share its links.',
  'rc.campaigns.colName': 'Campaign',
  'rc.campaigns.colStatus': 'Status',
  'rc.campaigns.colPeriod': 'Period',
  'rc.campaigns.colRoles': 'Roles',
  'rc.campaigns.colUndecided': 'To decide',
  'rc.campaigns.colAccepted': 'Accepted',
  'rc.campaign.back': 'Back to campaigns',
  'rc.campaign.messages': 'Messages to applicants',
  'rc.campaign.messagesHint':
    'Applicants get an email and a text when they apply and when you waitlist, accept or decline them. Change the texts, or write one per campaign, under Messages.',
  'rc.campaign.openMessages': 'Open messages',
  'rc.campaign.error': 'We could not load the campaign',
  'rc.campaign.name': 'Campaign name',
  'rc.campaign.namePlaceholder': 'For example PiP 2027 crew',
  'rc.campaign.open': 'Open for applications',
  'rc.campaign.resume': 'Resume',
  'rc.campaign.pause': 'Pause',
  'rc.campaign.close': 'Close',
  'rc.campaign.details': 'Details',
  'rc.campaign.intro': 'Introduction',
  'rc.campaign.introPlaceholder': 'What applicants should know first',
  'rc.campaign.opensOn': 'Opens',
  'rc.campaign.closesOn': 'Closes',
  'rc.campaign.roles': 'Roles',
  'rc.campaign.rolesHint':
    'Each role is one of your positions. Accepting someone gives them that position.',
  'rc.campaign.pickPosition': 'Pick a position',
  'rc.campaign.crewType': 'Works as',
  'rc.campaign.team': 'Team',
  'rc.campaign.teamHint': 'Its leads can propose applicants when team-lead input is on.',
  'rc.campaign.noTeam': 'No team',
  'rc.lead.proposedOnly': 'Proposed by a team lead',
  'rc.lead.proposed': 'Proposed',
  'rc.lead.proposals': 'Proposed by team leads',
  'rc.lead.settingsTitle': 'Team leads',
  'rc.lead.switch': 'Let team leads give input',
  'rc.lead.switchHint':
    "The leads of a role's team see its applicants, and can propose them or leave a note. You still decide. Pick each role's team in the campaign.",
  'rc.lead.switchedOn': 'Team leads can now give input.',
  'rc.lead.switchedOff': 'Team leads no longer see applicants.',
  'rc.lead.title': "Your team's applicants",
  'rc.lead.hint':
    'Applicants for the roles of the teams you lead. Propose the ones you want, or leave a note; the employer decides.',
  'rc.lead.yourTeams': 'Teams you lead: {teams}',
  'rc.lead.error': 'Could not load the applicants.',
  'rc.lead.off': 'Nothing to show',
  'rc.lead.offHint':
    'Applicants show here when the workspace lets team leads give input and you lead the team of a role.',
  'rc.lead.empty': 'No applicants waiting',
  'rc.lead.emptyHint': "New applicants for your team's roles show here.",
  'rc.lead.youProposed': 'You proposed',
  'rc.lead.propose': 'Propose',
  'rc.lead.proposeHint':
    "Tell the employer you want this person on your team. It goes into the application's history.",
  'rc.lead.proposedHint': 'You proposed this applicant. The employer decides.',
  'rc.lead.proposalNote': 'Why this person',
  'rc.lead.proposalNotePlaceholder': 'Why this person (optional)',
  'rc.lead.proposeButton': 'Propose for my team',
  'rc.lead.proposedToast': 'Proposed. The employer sees it in the pool.',
  'rc.lead.notes': 'Your notes',
  'rc.lead.notesHint': 'The employer reads them. You see only your own.',
  'rc.campaign.target': 'People needed',
  'rc.campaign.terms': 'Terms shown to applicants',
  'rc.campaign.termsPlaceholder': 'Pay, ticket, perks',
  'rc.campaign.nextSteps': 'Next steps after acceptance',
  'rc.campaign.nextStepsPlaceholder': 'What an accepted applicant does next',
  'rc.campaign.removeRole': 'Remove role',
  'rc.campaign.noRoles': 'No roles yet. Add the positions you recruit for.',
  'rc.campaign.addRole': 'Add role',
  'rc.campaign.save': 'Save changes',
  'rc.campaign.saved': 'Campaign saved.',
  'rc.campaign.saveFirst': 'Save your changes first.',
  'rc.links.title': 'Links',
  'rc.links.hint':
    'One link per place you share the campaign, so you see where applicants come from.',
  'rc.links.retired': 'Retired',
  'rc.links.retire': 'Retire link',
  'rc.links.empty': 'No links yet.',
  'rc.links.label': 'Label',
  'rc.links.labelPlaceholder': 'Label (optional)',
  'rc.links.add': 'Add link',
  'rc.join.notFound': 'This link does not work',
  'rc.join.notFoundHint': 'It may have been replaced. Ask the company for a new one.',
  'rc.join.error': 'We could not load this page. Try again in a moment.',
  'rc.join.yourApplication': 'Your application',
  'rc.join.status.applied': 'We have your application. You will hear from us.',
  'rc.join.status.waitlisted': 'You are on the waiting list. We will tell you if a place opens.',
  'rc.join.status.accepted': 'You are in. The company will tell you the next steps.',
  'rc.join.status.declined': 'Your application was not accepted this time.',
  'rc.join.status.withdrawn': 'You withdrew this application.',
  'rc.join.withdraw': 'Withdraw application',
  'rc.join.paused': 'Applications are paused for now. Try again later.',
  'rc.join.closed': 'This campaign is closed.',
  'rc.join.pickRole': 'Pick a role',
  'rc.join.apply': 'Apply',
  'rc.join.signIn': 'Sign in to apply',
  'rc.join.noAccount': 'No account yet?',
  'rc.join.signUp': 'Create one, then open this link again.',
  'error.recruiting.notFound': 'That no longer exists. Refresh and try again.',
  'error.recruiting.notOpen': 'This campaign is not taking applications right now.',
  'error.recruiting.alreadyMember': 'You already work for this company.',
  'error.recruiting.transition': 'That change is no longer possible. Refresh to see the latest.',
  'error.recruiting.invalid': 'Check the highlighted fields.',
  'error.recruiting.forbidden': 'Only an employer can manage recruiting.',
  'rc.type.text': 'Short text',
  'rc.type.longText': 'Long text',
  'rc.type.phone': 'Phone number',
  'rc.type.date': 'Date',
  'rc.type.choice': 'One choice',
  'rc.type.multiChoice': 'Several choices',
  'rc.type.yesNo': 'Yes or no',
  'rc.type.days': 'Days',
  'rc.type.file': 'File upload',
  'rc.q.phone': 'Phone number',
  'rc.q.birthDate': 'Date of birth',
  'rc.q.drivingLicence': 'Do you have a driving licence?',
  'rc.q.availableDays': 'Which days can you work?',
  'rc.form.title': 'Application form',
  'rc.form.standard': 'Standard questions',
  'rc.form.standardHint': 'Ask only what you need, and say why: applicants see the purpose.',
  'rc.form.required': 'Required',
  'rc.form.purpose': 'Purpose',
  'rc.form.purposePlaceholder': 'Why you ask (shown to applicants)',
  'rc.form.custom': 'Your own questions',
  'rc.form.label': 'Question',
  'rc.form.labelPlaceholder': 'Your question',
  'rc.form.type': 'Answer type',
  'rc.form.options': 'Options',
  'rc.form.optionsPlaceholder': 'One option per line',
  'rc.form.askOnly': 'Ask only:',
  'rc.form.remove': 'Remove',
  'rc.form.noCustom': 'No questions of your own yet.',
  'rc.form.addQuestion': 'Add question',
  'rc.form.days': 'Days to pick from',
  'rc.form.daysHint': 'The days applicants can say they are available.',
  'rc.form.addDay': 'Add day',
  'rc.form.consent': 'Consents and age',
  'rc.form.termsUrl': 'Link to your terms (https://…)',
  'rc.form.privacyUrl': 'Link to your privacy notice (https://…)',
  'rc.form.keepProfile': 'Optional: ask to keep the profile for next year',
  'rc.form.minAge': 'Minimum age (0 for none)',
  'rc.join.pickRoleFirst': 'Pick a role first.',
  'rc.join.aboutYou': 'About you',
  'rc.join.yourAccount': 'Your account',
  'rc.join.haveAccount': 'Already have a Celeri account?',
  'rc.join.signInInstead': 'Sign in to apply',
  'rc.join.firstName': 'First name',
  'rc.join.lastName': 'Last name',
  'rc.join.email': 'Email',
  'rc.join.password': 'Choose a password',
  'rc.join.passwordHint': 'At least 8 characters, with an uppercase letter and a digit.',
  'rc.join.confirmPassword': 'Repeat the password',
  'rc.join.accountExists':
    'This email already has an account. Sign in to apply; your answers are kept.',
  'rc.join.consents': 'Consent',
  'rc.join.acceptTerms': 'I accept the',
  'rc.join.terms': 'terms',
  'rc.join.acceptPrivacy': 'I have read the',
  'rc.join.privacy': 'privacy notice',
  'rc.join.applyAndCreate': 'Apply and create my account',
  'rc.join.confirmEmail':
    'We have your application. Check your email and confirm your account, then sign in to follow it.',
  'rc.join.yes': 'Yes',
  'rc.join.pickFile': 'Choose a file',
  'rc.join.uploading': 'Uploading…',
  'rc.join.fileHint': 'PDF, JPG, PNG or HEIC, up to 10 MB.',
  'rc.join.fileTooLarge': 'The file is larger than 10 MB.',
  'rc.join.fileFailed': 'The file could not be uploaded. Try again.',
  'rc.join.removeFile': 'Remove {name}',
  'rc.join.removeFileShort': 'Remove',
  'rc.join.no': 'No',
  'rc.app.emailUnconfirmed': 'Email not confirmed',
  'rc.app.noAnswers': 'No answers.',
  'rc.app.consented': 'Consented to',
  'rc.pool.open': 'Open applicants',
  'rc.pool.export': 'Export CSV',
  'rc.pool.filters': 'Filters',
  'rc.pool.search': 'Search name or email',
  'rc.pool.age': 'Age',
  'rc.pool.ageFrom': 'From',
  'rc.pool.ageTo': 'To',
  'rc.pool.ageHint': 'From the date of birth. Applicants who gave none are left out.',
  'rc.pool.clear': 'Clear filters',
  'rc.pool.selected': '{count} selected',
  'rc.pool.someSelected': '{count} of {total} selected.',
  'rc.pool.selectAll': 'Select all {total}',
  'rc.pool.noMatch': 'No applicants match the filters.',
  'rc.pool.colApplied': 'Applied',
  'rc.pool.sort': 'Sort',
  'rc.pool.sortNewest': 'Newest first',
  'rc.pool.sortOldest': 'Oldest first',
  'rc.pool.sortName': 'Name',
  'rc.pool.confirmAccept':
    'Accept {count} applicants? Each gets access to the workspace with the position of the role they applied for.',
  'rc.pool.confirmWaitlist': 'Put {count} applicants on the waitlist?',
  'rc.pool.confirmDecline': 'Decline {count} applicants? A decline is final.',
  'rc.pool.reason': 'Reason (optional, kept in the history)',
  'rc.pool.reasonPlaceholder': 'For example: the bar team is full',
  'rc.pool.bulkDone': 'Changed: {count}.',
  'rc.pool.bulkFailed': 'Could not change {count}; they stay selected.',
  'rc.pool.answers': 'Answers',
  'rc.pool.history': 'History',
  'rc.pool.notes': 'Notes',
  'rc.pool.notesHint':
    'Only your company sees notes. The applicant can ask for a copy of their data, notes included, so write them with that in mind.',
  'rc.pool.notePlaceholder': 'Write a note…',
  'rc.pool.addNote': 'Add note',
  'rc.pool.deleteNote': 'Delete note',
  'rc.funnel.open': 'Funnel',
  'rc.funnel.title': 'Funnel',
  'rc.funnel.from': 'From',
  'rc.funnel.to': 'To',
  'rc.funnel.allCrew': 'All crew types',
  'rc.funnel.hint':
    'Visits and starts are counted anonymously, from when counting began; applications are counted from the start.',
  'rc.funnel.error': 'The funnel could not be loaded.',
  'rc.funnel.stages': 'Stages',
  'rc.funnel.visited': 'Visited',
  'rc.funnel.started': 'Started',
  'rc.funnel.applied': 'Applied',
  'rc.funnel.accepted': 'Accepted',
  'rc.funnel.of': 'of {stage}',
  'rc.funnel.perDay': 'Applications per day',
  'rc.funnel.perDayLabel': 'Applications per day, as columns; the table below has the numbers.',
  'rc.funnel.noApplications': 'No applications in these days.',
  'rc.funnel.showTable': 'Show the days as a table',
  'rc.funnel.day': 'Day',
  'rc.funnel.perSource': 'By source',
  'rc.funnel.noSources': 'Nothing yet: no visits or applications in these days.',
  'rc.funnel.acceptRate': 'Accepted of applied',
  'rc.funnel.unknownLink': 'Link',
  'rc.funnel.perCrew': 'By crew type',
  'rc.invites.title': "Last season's crew",
  'rc.invites.open': 'Invite and see answers',
  'rc.invites.cardHint':
    "Ask last season's crew first: join again, keep their profile for later, or have it deleted.",
  'rc.invites.hint':
    "Each person gets a personal link by email and SMS, working until the campaign closes. Those who join apply through the campaign's Returning link and are marked returning in the pool. Deleting takes them out of this workspace; their account and past shifts stay.",
  'rc.invites.openFirst': 'Open the campaign before inviting: those who join apply through it.',
  'rc.invites.invite': "Invite last season's crew",
  'rc.invites.error': 'The invites could not be loaded.',
  'rc.invites.empty': 'No one invited yet.',
  'rc.invites.answers': 'Answers',
  'rc.invites.invited': 'Invited',
  'rc.invites.join': 'Join again',
  'rc.invites.keep': 'Keep profile only',
  'rc.invites.delete': 'Delete profile',
  'rc.invites.noAnswer': 'No answer yet',
  'rc.invites.appliedOf': '{count} applied',
  'rc.invites.applied': 'Applied',
  'rc.invites.sentAt': 'Sent',
  'rc.invites.answer': 'Answer',
  'rc.invites.answeredAt': 'Answered',
  'rc.invites.pickTitle': "Invite last season's crew",
  'rc.invites.workedIn': 'Worked shifts in',
  'rc.invites.pickHint':
    'Everyone with a published shift that year, except employers. Untick anyone you do not want to ask.',
  'rc.invites.nobody': 'Nobody worked a published shift that year.',
  'rc.invites.everyone': 'Everyone ({count})',
  'rc.invites.shifts': '{count} shifts',
  'rc.invites.alreadyInvited': 'Invited',
  'rc.invites.willGet': 'Each gets an email and an SMS.',
  'rc.invites.inviteCount': 'Invite {count}',
  'rc.invites.sent': 'Invites sent: {count}.',
  'rc.invites.returning': 'Returning',
  'rc.invites.returningOnly': "Last season's crew only",
  'rc.invites.lastSeason': 'Last season: {position}',
  'rc.invite.pageTitle': 'Join us again?',
  'rc.invite.title': 'Hi {name}!',
  'rc.invite.intro':
    'Thank you for last season. {company} is putting together the crew for {campaign}, and asks you first.',
  'rc.invite.choose': 'What would you like? Answer by {date}.',
  'rc.invite.change': 'You can change your answer:',
  'rc.invite.join': 'Join again: apply now',
  'rc.invite.keep': 'Keep my profile for later',
  'rc.invite.delete': 'Delete my profile here',
  'rc.invite.deleteConfirm':
    'This takes you out of {company} now: you lose access and your applications here are deleted. Your Celeri account stays, and so do the shifts you worked.',
  'rc.invite.deleteNow': 'Delete it',
  'rc.invite.deleted': 'Done. You are no longer part of {company}.',
  'rc.invite.accountStays':
    'Your Celeri account stays. To delete it as well, sign in and use Delete account on your profile.',
  'rc.invite.joinedApply': 'Great! Apply now: sign in and pick your role.',
  'rc.invite.joinedLater': 'Great! Applications are not open yet; we will let you know.',
  'rc.invite.apply': 'Apply',
  'rc.invite.kept': 'Thanks! We keep your profile for later.',
  'rc.invite.failed': 'Your answer could not be saved. Please try again.',
  'rc.invite.expired': 'This link has expired',
  'rc.invite.expiredHint': 'Ask the company for a new invite.',
  'rc.invite.notFoundHint': 'Check that you opened the whole link from your invite.',
  'rc.invite.privacy': 'How your data is handled',
  'error.recruiting.accountExists': 'This email already has an account. Sign in to apply.',
  'error.recruiting.notSent': 'The application could not be sent. Please try again.',
} as const;

export type TranslationKey = keyof typeof EN;

/** Whether a string is one of the keys above, for values that may be either. */
export function isTranslationKey(value: string): value is TranslationKey {
  return value in EN;
}
